// Módulos de la portada Pulse — composición por rol (espejo de docs/02-roles-portadas.md).
// En fase 1 real, cada módulo lee de /api/pulse/home; hoy leen los datos demo.
import React, { useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { G, HOTELS, type Alerta, type RoleId } from '@/constants/data';
import {
  api, type AgeingLive, type AssetKpisLive, type Brief, type CajaLive, type ComisionesLive,
  type HabEstadoLive, type HotelDiaLive, type HotelRow,
  type PickupLive, type RejillaLive, type SistemasKpis, type SistemasLive, type Vivo,
} from '@/api/client';
import { AGEING_COLORS, budgetColor, C, F, heatColor, HEAT_RAMP } from '@/constants/theme';
import { fmt, fmtDec, fmtDiaMes, fmtE, fmtK, fmtM, pct1 } from '@/lib/format';
import OnaIcon, { type IconName } from './OnaIcon';
import { Cargando } from './MarcaOna';
import EnsenarNoa from './EnsenarNoa';
import PremioCard from '@/components/PremioCard';
import { useRole } from '@/context/RoleContext';
import { suscribir, valorActual } from '@/lib/refresco';
import { AbreKpi, Bar, BottomSheet, Card, Chip, KpiTile, Row, SectionTitle, SegBar, Spark, StatusDot, ZoomDetalle } from './ui';

const I = (name: IconName, color = C.muted) => <OnaIcon name={name} size={17} color={color} />;

/* Trae un bloque que SOLO existe en vivo. Tres estados y ninguno miente:
   cargando (no se sabe todavía), motivo (el servidor dice por qué no) y el dato. */
function useLive<T>(cargar: () => Promise<Vivo<T>>, refrescoMs?: number, recarga = 0) {
  const [dato, setDato] = useState<T | null>(null);
  const [motivo, setMotivo] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  // El tirón hacia abajo cuenta como una recarga más.
  const [global, setGlobal] = useState(valorActual());
  useEffect(() => suscribir(setGlobal), []);
  useEffect(() => {
    let vivo = true;
    let reintento: ReturnType<typeof setTimeout> | null = null;
    /* 🔴 UN FALLO NO BORRA LA PANTALLA, Y SE REINTENTA EN SEGUNDOS (26-08).
       Patricio: la tarjeta del grupo enseñó el cargando «y después directamente
       me desapareció; tuve que forzar cerrar la app». La secuencia era esta:
       una petición falla UNA vez (un parpadeo de red, o el API reiniciándose —
       esa mañana se estaba desplegando), esto hacía `setDato(null)`, la tarjeta
       veía «sin datos» y se esfumaba… y el siguiente intento era el refresco de
       los 10 MINUTOS. Forzar el cierre «lo arreglaba» porque remontar relanza
       la petición. Dos cambios:
         · el dato que ya se tenía SE CONSERVA — un refresco fallido no puede
           borrar lo que el usuario está mirando; el dato lleva sus propias
           marcas de hora, así que no miente;
         · al fallar se reintenta a los 12 segundos, no a los 10 minutos. Cada
           fallo programa UN reintento (no se apilan), así que mientras el
           servidor no esté, esto llama una vez cada 12 s — con diez usuarios de
           dirección es inofensivo y se recupera solo en cuanto vuelve. */
    const leer = () => cargar().then((r) => {
      if (!vivo) return;
      if (r.ok) {
        setDato(r.dato); setMotivo(null);
      } else {
        setMotivo(r.motivo);
        if (reintento) clearTimeout(reintento);
        reintento = setTimeout(leer, 12_000);
      }
      setCargando(false);
    }).catch(() => {
      if (!vivo) return;
      setMotivo('No se ha podido contactar con el servidor.');
      setCargando(false);
      if (reintento) clearTimeout(reintento);
      reintento = setTimeout(leer, 12_000);
    });
    leer();
    const t = refrescoMs ? setInterval(leer, refrescoMs) : null;
    return () => { vivo = false; if (t) clearInterval(t); if (reintento) clearTimeout(reintento); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recarga, global]);
  return { dato, cargando, motivo };
}

/* Un bloque en vivo que hoy no se puede pintar. Se dice, y se dice por qué:
   una tarjeta en blanco parece un fallo de la app y un 0 parece un dato. */
/* 🔴 CARGANDO Y «NO HAY DATO» NO SE PINTAN IGUAL. Los dos usaban este mismo
   cartel de texto, así que «Leyendo la cartera…» y «La cartera no responde» se
   veían idénticos: el mismo gris quieto. Con `cargando`, la espera lleva el
   latido —algo se mueve, o sea que algo está pasando— y el hueco de verdad se
   queda como estaba, quieto, que es lo que tiene que parecer. */
function SinDato({ titulo, motivo, dot, cargando }: {
  titulo: string; motivo: string; dot?: string; cargando?: boolean;
}) {
  return (
    <Card>
      <SectionTitle dot={dot}>{titulo}</SectionTitle>
      {cargando
        ? <Cargando texto={motivo} alto={96} />
        : <Text style={m.sinDato}>{motivo}</Text>}
    </Card>
  );
}

/* ---------- comunes ---------- */

function KpiTrio({ brief }: { brief?: Brief | null }) {
  const { puede } = useRole();
  if (!puede('PRODUCCION')) return null;
  // 🔴 Estos tres eran cifras inventadas del prototipo. Ahora salen del brief:
  // ocupación y ADR los da el servidor, y el RevPAR es su producto — que es
  // literalmente su definición, no una estimación.
  if (brief && (brief.occ != null || brief.adr != null)) {
    const occ = brief.occ, adr = brief.adr ?? null;
    const revpar = occ != null && adr != null ? (adr * occ) / 100 : null;
    const filasOcc: Array<[string, string]> = (brief.occPorPms ?? []).map((o) => [`${o.pms} · ${o.hoteles} hot.`, `${fmtDec(o.media)}%`]);
    const filasAdr: Array<[string, string]> = (brief.adrPorPms ?? []).map((a) => [`${a.pms} · ${fmt(a.rn)} noches`, `${fmtDec(a.adr)} €`]);
    return (
      <View style={m.kpis}>
        <KpiTile value={occ != null ? pct1(occ) : '—'} label="Ocupación hoy" delta={`${brief.hoteles} hoteles`} deltaKind="flat"
          detail={filasOcc.length ? { rows: filasOcc, foot: 'Rejilla de los 4 PMS, cada uno con su doctrina.' } : undefined} />
        <KpiTile value={adr != null ? `${fmtDec(adr)} €` : '—'} label="ADR del mes" delta="neto sin IVA" deltaKind="flat"
          detail={filasAdr.length ? { rows: filasAdr, foot: 'Alojamiento ÷ noches, del folio (cuenta huésped) de cada PMS.' } : undefined} />
        <KpiTile value={revpar != null ? `${fmtDec(revpar)} €` : '—'} label="RevPAR" delta="ADR × ocupación" deltaKind="flat" />
      </View>
    );
  }
  const foot = 'Módulo demo — el detalle real por PMS llega con la siguiente iteración del BFF.';
  return (
    <View style={m.kpis}>
      <KpiTile
        value={pct1(G.occ)} label="Ocupación hoy" delta="▲ +2,1 pts"
        detail={{
          rows: [['Noray · 12 hot.', '89,2%'], ['Onasystem · 2 hot.', '82,5%'], ['Navision · 2 hot.', '80,5%'], ['Avalon · 1 hot.', '92,8%']],
          foot,
        }}
      />
      <KpiTile
        value={`${fmtDec(G.adr)} €`} label="ADR" delta="▲ +4,2%"
        detail={{
          rows: [['Noray', '118,6 €'], ['Onasystem', '96,2 €'], ['Navision', '86,4 €'], ['Avalon', '112,4 €']],
          foot,
        }}
      />
      <KpiTile
        value={`${fmtDec(G.revpar)} €`} label="RevPAR" delta="▲ +6,1%"
        detail={{
          rows: [['Fórmula', 'ADR × ocupación'], ['Noray', '105,8 €'], ['Avalon', '104,3 €'], ['Resto', '—']],
          foot,
        }}
      />
    </View>
  );
}

/* El GOP proyectado del prototipo (2,94 M€ «vs budget») se ha ELIMINADO: era
   una cifra inventada en la portada de Dirección, al lado de datos reales, y
   alguien acabaría creyéndosela. Cuando exista una fuente de verdad para el
   GOP del mes en curso, vuelve — pero con datos, no con un dibujo.
   El GOP real del último mes PUBLICADO sí está: es la tarjeta de Premios Noa. */

/* 🔴 CABEZA Y COLA: SOLO HOTELES DE CONTROL DE GESTIÓN, y al tocar, los que
   quedan fuera. Acuerdo de la demo (docs/15-demo-dg.md).

   Por qué importa quién entra: comparar «cabeza y cola» mezclando hoteles que
   hacen el cierre hotelero con hoteles que no lo hacen es comparar peras con
   manzanas — y la cola se llenaba de los que no tienen presupuesto, que es
   precisamente donde el CEO va a mirar. Los de CG son 39, y son EXACTAMENTE los
   39 que tienen presupuesto en `pl_budget_real`: el mismo conjunto por dos
   caminos distintos, que es la única validación que vale.

   Y los de fuera NO se esconden. Son 14 que facturan 4,2 M€ del mes —el 16% del
   grupo, y la mitad es Ona Don Juan él solo, al 99,5% de ocupación—. Patricio:
   «esos hoteles tendrán que ir dentro de Otros Hoteles». Esconder 4,2 M€ deja un
   agujero que se ve; agruparlos y decir por qué, no. */
function TopBottom({ live }: { live?: HotelRow[] }) {
  // Sin permiso de producción esta tarjeta no tiene nada honesto que enseñar.
  const { puede } = useRole();
  const [abierta, setAbierta] = useState(false);
  if (!puede('PRODUCCION')) return null;
  // Sin Tortugonas: el premio irá sobre el último mes PUBLICADO de CG, no el vivo.
  // Con datos LIVE la tarjeta usa SOLO los hoteles reales (activos). Mientras el
  // budget 2026 no esté cargado, ordena por producción del mes y lo declara.
  const rows: Array<{ n: string; bud: number | null; mes: number; cg: boolean }> = live?.length
    ? live.map((h) => ({ n: h.n, bud: h.bud ?? null, mes: h.mes, cg: h.cg !== false }))
    : HOTELS.map((h) => ({ n: h.n, bud: h.bud, mes: h.mes, cg: true }));
  /* `cg !== false` y no `cg === true`: en demo el campo no viene, y un hotel sin
     marca se trata como que SÍ entra. Si un día el API deja de mandarlo, la
     tarjeta vuelve a lo de antes en vez de quedarse vacía. */
  const enCg = rows.filter((r) => r.cg);
  const fuera = rows.filter((r) => !r.cg).sort((a, b) => b.mes - a.mes);
  const hayBudget = enCg.some((r) => r.bud != null);
  const sorted = [...enCg].sort((a, b) => (hayBudget ? (b.bud ?? -1) - (a.bud ?? -1) : b.mes - a.mes));
  const mesNombre = new Date().toLocaleDateString('es-ES', { month: 'long' });
  const item = (h: (typeof rows)[0], gold: boolean, last: boolean) => (
    <View key={h.n} style={[m.tbRow, last && { borderBottomWidth: 0 }]}>
      <Text style={[m.tbMedal, gold && { color: C.good }]}>{gold ? '△' : '▽'}</Text>
      <Text style={m.tbName} numberOfLines={1}>{h.n.replace('Ona ', '')}</Text>
      {hayBudget && h.bud != null ? (
        <Chip kind={h.bud >= 100 ? 'good' : 'bad'}>{`${h.bud >= 100 ? '+' : ''}${fmtDec(h.bud - 100)}%`}</Chip>
      ) : (
        <Chip kind={gold ? 'good' : 'mut'}>{`${fmt(Math.round(h.mes / 1e3))} k€`}</Chip>
      )}
    </View>
  );

  /* LA CARA DE ATRÁS. No es «el resto de la lista»: es una respuesta a «¿y estos
     por qué no están?», que es lo que va a preguntar quien conozca los hoteles. */
  if (abierta) {
    const total = fuera.reduce((t, h) => t + h.mes, 0);
    return (
      <Pressable onPress={() => setAbierta(false)}>
        <Card>
          <SectionTitle right="Otros Hoteles">Fuera de Control de Gestión</SectionTitle>
          <Text style={m.footNote}>
            Estos hoteles facturan, pero no hacen el cierre hotelero mensual, así que
            no tienen presupuesto contra el que compararse. Por eso no entran en la
            cabeza y cola.
          </Text>
          <View style={{ marginTop: 10 }}>
            {fuera.map((h, i) => (
              <View key={h.n} style={[m.tbRow, i === fuera.length - 1 && { borderBottomWidth: 0 }]}>
                <Text style={m.tbName} numberOfLines={1}>{h.n.replace('Ona ', '')}</Text>
                <Chip kind="mut">{`${fmt(Math.round(h.mes / 1e3))} k€`}</Chip>
              </View>
            ))}
          </View>
          <View style={{ backgroundColor: C.card2, borderRadius: 10, padding: 10, marginTop: 10 }}>
            <Text style={{ fontFamily: F.uiMed, fontSize: 12.5, color: C.ink }}>
              {`${fuera.length} hotel${fuera.length === 1 ? '' : 'es'} · ${fmtE(total)} de producción del mes`}
            </Text>
          </View>
          <Text style={m.footNote}>
            Las centrales y las fichas «Hotel PT» y «Hotel RE» no cuentan como hotel:
            no salen en ninguna de las dos caras.
          </Text>
          <Text style={m.tbVolver}>toca para volver ▴</Text>
        </Card>
      </Pressable>
    );
  }

  return (
    <Pressable onPress={() => (fuera.length ? setAbierta(true) : undefined)}>
      <Card>
        <SectionTitle right={`${enCg.length} de CG`}>
          {hayBudget ? `Contra budget · ${mesNombre}` : `Producción · ${mesNombre} · top y cola`}
        </SectionTitle>
        {sorted.slice(0, 3).map((h) => item(h, true, false))}
        <View style={{ height: 8 }} />
        {sorted.slice(-3).reverse().map((h, i) => item(h, false, i === 2))}
        {!hayBudget && live?.length ? (
          <Text style={m.footNote}>El «contra budget» se enciende solo cuando se cargue el budget 2026 (plantillas Revenue).</Text>
        ) : null}
        {fuera.length ? (
          <Text style={m.tbVolver}>
            {`Solo hoteles de Control de Gestión · toca para ver los ${fuera.length} que quedan fuera ▾`}
          </Text>
        ) : null}
      </Card>
    </Pressable>
  );
}

function AlertPeek({ filter }: { filter?: string }) {
  // Las mismas alertas que cuenta la campanita: overbooking de los 4 PMS y
  // sincronizaciones caídas. Nada escrito a mano.
  /* 🔴 `api.alertas()` cae a los avisos de ejemplo cuando el servidor no
     contesta. Sin mirar `source` la tarjeta enseñaría los de mentira SIN la
     franja ámbar — es decir, peor que antes de conectarla. Se arrastra la
     procedencia y la franja se enciende sola. */
  const { dato, cargando } = useLive<{ v: Alerta[]; demo: boolean }>(
    () => api.alertas().then((r) => ({ ok: true as const, dato: { v: r.data, demo: r.source !== 'live' } })),
    5 * 60e3);
  const todas = dato?.v ?? [];
  const esDemo = dato?.demo ?? false;
  /* 🔴 `dep` ERA UN REPARTO PROPIO DE PULSE Y LAS DE HOTELOS NO LO TRAEN.
     Desde que los avisos vienen de `dbo.notificaciones`, quien decide quién los
     ve son las cinco puertas del servidor (usuario, hotel, módulo, preferencia y
     capacidad). Un aviso sin `dep` YA está dirigido: si además se le exigiera
     pasar este filtro, la tarjeta de la portada de Sistemas se quedaría vacía
     —«nada pendiente en tu área»— justo con los fallos de sync delante. */
  const mios = todas.filter((a) => !filter || !a.dep || a.dep.includes(filter));
  const list = mios.slice(0, 3);

  if (cargando) return <SinDato cargando titulo="Requiere atención" dot={C.warn} motivo="Comprobando…" />;
  if (!list.length) {
    return (
      <Card demo={esDemo}>
        <SectionTitle dot={C.good}>Requiere atención</SectionTitle>
        <Text style={m.sinDato}>
          {todas.length
            ? `Nada pendiente en tu área. Hay ${todas.length} aviso${todas.length === 1 ? '' : 's'} en otras.`
            : 'Todo en orden: ni overbooking ni sincronizaciones caídas.'}
        </Text>
      </Card>
    );
  }

  const resto = mios.length - list.length;
  return (
    <Card demo={esDemo}>
      <SectionTitle dot={C.warn} right={resto > 0 ? `+${resto} más →` : undefined}>Requiere atención</SectionTitle>
      {list.map((a, i) => (
        <View key={a.t + i} style={[m.tbRow, i === list.length - 1 && { borderBottomWidth: 0 }]}>
          <View style={{ width: 26, alignItems: 'center' }}>
            {I((a.ic || 'warn') as IconName, a.sev === 'crit' ? C.bad : C.aquaSoft)}
          </View>
          <View style={{ flex: 1 }}>
            <Text style={m.tbName} numberOfLines={1}>{a.t}</Text>
            <Text style={m.subTxt} numberOfLines={2}>{a.m}</Text>
          </View>
        </View>
      ))}
    </Card>
  );
}

/* ---------- Dirección de hotel / regional ---------- */

/* MI HOTEL — el día por punto de venta, tal como sale en Facturación del día.
   Es la pantalla que un director mira por la tarde para saber cómo va el día. */
function MiHotel({ hoteles }: { hoteles?: HotelRow[] }) {
  const mio = (hoteles ?? [])[0];
  const { dato, cargando, motivo } = useLive<HotelDiaLive>(
    () => (mio?.id != null
      ? api.hotelDia(mio.id)
      : Promise.resolve({ ok: false as const, motivo: 'Todavía no sé qué hotel es el tuyo.' })),
    10 * 60e3);

  if (!mio) return <SinDato titulo="Tu hotel" motivo="Tu cuenta no tiene ningún hotel asignado en Hotelos. Pídeselo a Sistemas." />;
  if (cargando) return <SinDato cargando titulo={mio.n.replace(/^Ona /i, '')} motivo="Leyendo los folios (cuenta huésped) del día…" />;
  if (!dato) return <SinDato titulo={mio.n.replace(/^Ona /i, '')} motivo={motivo ?? 'No hay folios (cuenta huésped) de este hotel.'} />;
  if (!dato.hayDatos) {
    return (
      <SinDato
        titulo={`${dato.hotel} · hoy`}
        motivo={`Todavía no hay folios (cuenta huésped) del día. El último con datos es el ${dato.ultimoConDatos ?? '—'}.`}
      />
    );
  }

  const bloque = (t: string, arr: typeof dato.recepcion, sub: { sinIva: number; lineas: number }) =>
    arr.length ? (
      <View style={{ marginTop: 12 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Text style={m.tramoT}>{t}</Text>
          <Text style={m.tramoV}>{fmtE(sub.sinIva)}</Text>
        </View>
        {arr.slice(0, 8).map((p) => (
          <View key={p.punto} style={m.cliRow}>
            <Text style={m.cliN} numberOfLines={1}>{p.punto}</Text>
            <Text style={m.cliV}>{fmtE(p.sinIva)}</Text>
          </View>
        ))}
      </View>
    ) : null;

  return (
    <Card>
      <SectionTitle right={dato.ultSync ? `hasta las ${dato.ultSync}` : undefined}>
        {`${dato.hotel.replace(/^Ona /i, '')} · hoy`}
      </SectionTitle>
      <Text style={m.heroNum}>{fmtE(dato.totalSin)}</Text>
      <Text style={m.subTxt}>neto sin IVA · {fmt(dato.lineas)} líneas</Text>
      {bloque('Recepción', dato.recepcion, dato.subRecepcion)}
      {bloque('POS / F&B', dato.pos, dato.subPos)}
      <Text style={m.footNote}>{dato.nota}</Text>
    </Card>
  );
}

/* MIS HOTELES — para el regional: los suyos, comparables entre sí. No es el
   ranking del grupo recortado: es su zona, con su total y su peso. */
function MisHoteles({ hoteles }: { hoteles?: HotelRow[] }) {
  const hs = [...(hoteles ?? [])].sort((a, b) => b.mes - a.mes);
  if (!hs.length) {
    return <SinDato titulo="Tus hoteles" motivo="Tu cuenta no tiene hoteles asignados en Hotelos. Pídeselo a Sistemas." />;
  }
  const total = hs.reduce((a, h) => a + h.mes, 0);
  return (
    <Card>
      <SectionTitle right={`${hs.length} hoteles`}>{`Tu zona · ${fmtM(total)} en el mes`}</SectionTitle>
      {hs.slice(0, 10).map((h, i) => (
        <View key={h.id ?? h.n} style={[m.tbRow, i === Math.min(hs.length, 10) - 1 && { borderBottomWidth: 0 }]}>
          <Text style={m.tbMedal}>{i + 1}</Text>
          <View style={{ flex: 1 }}>
            <Text style={m.tbName} numberOfLines={1}>{h.n.replace(/^Ona /i, '')}</Text>
            <Text style={m.subTxt}>
              {h.occ != null ? `ocupación ${pct1(h.occ)}` : 'sin ocupación'}
              {h.adr != null ? ` · ADR ${fmtDec(h.adr)} €` : ''}
              {h.d == null ? ' · ayer sin volcar' : ''}
            </Text>
          </View>
          <Text style={m.wfVal}>{fmtK(h.mes)}</Text>
        </View>
      ))}
      <Text style={m.footNote}>
        Solo tus hoteles: el alcance sale de Hotelos. Para añadir o quitar zonas, se cambia allí.
      </Text>
    </Card>
  );
}

/* ---------- Operaciones ---------- */

function OpsToday() {
  return (
    <View style={m.kpis}>
      <KpiTile value={fmt(G.lleg)} label="Llegadas hoy" delta="pico 12–14 h" deltaKind="flat" />
      <KpiTile value={fmt(G.sal)} label="Salidas" delta="86% antes de 11 h" deltaKind="flat" />
      <KpiTile value={fmt(G.inHouse)} label="Huéspedes in-house" delta={`▲ ${pct1(G.occ)} ocup.`} />
    </View>
  );
}

/* ---------- Estado de habitaciones ---------- */

/* La foto de gobernanta del PMS, cada 10 minutos. Hasta el 31-08 esta tarjeta
   enseñaba Limpias 412 / Sucias 186 / Retenidas 23 / No usar 9, inventados.

   🔴 Y NO SE CONECTÓ TAL CUAL, PORQUE LA FORMA ESTABA MAL, no solo los números:
     · «Retenidas» y «No usar» iban en la MISMA barra que limpias y sucias, y el
       propio informe de Hotelos dice que «una habitación bloqueada CONSERVA su
       estado de limpieza». Son dos ejes: sumarlos cuenta habitaciones dos veces.
       Ahora las bloqueadas van en su propia línea, debajo y separadas.
     · Y no se da un «% de sucias del grupo». Medido sobre las 6.256 reales,
       «sin hacer» no significa lo mismo en todos los hoteles: en 8 no hay ciclo
       diario (apart-hoteles de socios) y en el resto incluye habitaciones
       VENDIDAS. Palm Beach: 255 vendidas, 296 sin hacer, 24 listas. Un hotel de
       320 no opera con 296 habitaciones sucias a las seis de la tarde.
   Así que la tarjeta cuenta LO QUE ESTÁ LISTO —que significa lo mismo en todos
   los hoteles— y saca aparte la única excepción que es un problema bajo
   cualquiera de las dos lecturas: habitaciones vendidas que siguen sin hacer. */
function Rooms() {
  const [abierto, setAbierto] = useState(false);
  const { dato, cargando, motivo } = useLive<HabEstadoLive>(() => api.habitacionesEstado());
  if (cargando) return <SinDato cargando titulo="Estado de habitaciones · ahora" motivo="Leyendo la foto del PMS…" />;
  if (!dato) return <SinDato titulo="Estado de habitaciones · ahora" motivo={motivo || 'El servidor no ha dado la foto del PMS.'} />;

  const g = dato.grupo;
  const bl = dato.bloqueos;
  const hora = dato.foto
    ? new Date(dato.foto).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
    : null;
  const segs = [
    { l: 'Listas', v: g.listas, color: C.aqua },
    { l: 'Pendientes', v: g.pendientes, color: C.gold },
    ...(g.otras ? [{ l: 'Sin identificar', v: g.otras, color: C.muted }] : []),
  ].filter((x) => x.v > 0);
  const pctListas = g.habs ? Math.round((100 * g.listas) / g.habs) : null;
  /* Sin los de socios: el chip cuenta los mismos hoteles que suma el titular. */
  const peores = dato.hoteles.filter((h) => !h.sinCiclo && (h.vendidasSinHacer ?? 0) > 0);

  const tarjeta = (
    <Card>
      <SectionTitle right={hora ? `foto ${hora}` : undefined}>Estado de habitaciones · ahora</SectionTitle>
      {/* 🔴 EL NÚMERO GRANDE ES «LISTAS», Y ANTES NO LO ERA. En la primera versión
          la barra pintaba «Pendientes 3.010» en ámbar y más ancho que las listas,
          así que lo primero que se leía era justo el número que había decidido NO
          destacar: el que mezcla la habitación que salió esta mañana con la que
          nadie ha vendido en tres semanas. El titular y el razonamiento tienen que
          decir lo mismo. */}
      <View style={m.habHero}>
        <Text style={m.habPct}>{pctListas != null ? `${pctListas}%` : '—'}</Text>
        <View style={{ flex: 1 }}>
          <Text style={m.habHeroT}>listas para entregar</Text>
          <Text style={m.subTxt}>
            {`${fmt(g.listas)} de ${fmt(g.habs)} en ${g.hoteles} hoteles con ciclo diario`}
          </Text>
        </View>
      </View>
      {segs.length ? <SegBar segs={segs} /> : null}
      <View style={m.legGrid}>
        {segs.map((x) => (
          <View key={x.l} style={m.legItem}>
            <View style={[m.legDot, { backgroundColor: x.color }]} />
            <Text style={m.legTxt}>{x.l} <Text style={m.legVal}>{fmt(x.v)}</Text></Text>
          </View>
        ))}
      </View>

      {/* La excepción, y la única línea en rojo: bajo las dos lecturas posibles
          —no se ha limpiado, o el PMS no se está actualizando— es un problema. */}
      {g.vendidasSinHacer > 0 ? (
        <Row icon={I('warn', C.bad)} label="Vendidas y aún sin hacer" value={`≥ ${fmt(g.vendidasSinHacer)}`}
          chip={peores.length ? `${peores.length} hoteles` : undefined} />
      ) : null}
      {/* Segundo eje, separado a propósito: una bloqueada conserva su estado. */}
      <Row icon={I('key')} label="Bloqueadas ahora" value={fmt(bl.vigentes)}
        chip={bl.motivos[0] ? `${bl.motivos[0].motivo.toLowerCase()} ${fmt(bl.motivos[0].n)}` : undefined}
        last={!g.enLimpieza} />
      {/* Si hay alguien limpiando AHORA, es la señal de que la pantalla se está
          usando en este momento. Con 0 no se enseña: una línea a cero de noche no
          informa de nada. */}
      {g.enLimpieza > 0 ? (
        <Row icon={I('clock')} label="En limpieza ahora mismo" value={fmt(g.enLimpieza)} last />
      ) : null}
      <Text style={m.footNote}>
        {'Foto del PMS de cada hotel, se refresca cada 10 min'
          + (dato.sinCiclo.habs ? ` · ${fmt(dato.sinCiclo.habs)} habs de socios contadas aparte` : '')}
      </Text>
      <Text style={m.tbVolver}>{`toca para ver los ${dato.hoteles.length} hoteles, uno a uno ▾`}</Text>
    </Card>
  );

  if (!dato.hoteles.length) return tarjeta;

  return (
    <>
      <Pressable onPress={() => setAbierto(true)}>{tarjeta}</Pressable>
      <ZoomDetalle
        open={abierto} onClose={() => setAbierto(false)}
        valor={pctListas != null ? `${pctListas}%` : '—'} etiqueta="Habitaciones listas"
        pie={'Foto del estado de pisos de Noray, una fila por habitación, refrescada cada 10 min. '
           + '«Vendidas y sin hacer» es una COTA INFERIOR: el espejo dice cuántas están vendidas, no cuáles. '
           + 'Ordenados por ahí; los apart-hoteles de socios, al final y sin ese número.'}
      >
        {dato.hoteles.map((h) => {
          /* Mismo recorte de nombre que la tarjeta del grupo: los prefijos que no
             distinguen a nadie se van, y así cabe la cifra en una línea. */
          const nom = h.hotel.replace(/^(Ona|Hotel|Be ?live)\s+/i, '').replace(/\s*\(.*\)\s*$/, '');
          const pl = h.habs ? (100 * h.listas) / h.habs : 0;
          const pe = h.habs ? (100 * h.enLimpieza) / h.habs : 0;
          const vsh = h.sinCiclo ? null : (h.vendidasSinHacer ?? 0);
          return (
            <View key={h.id} style={{ paddingVertical: 7, borderBottomWidth: 1, borderBottomColor: C.line }}>
              <View style={m.miniRow}>
                <Text style={[m.miniLbl, { maxWidth: '46%', color: C.ink }]} numberOfLines={1}>{nom}</Text>
                <Text style={[m.miniVal, { flex: 1 }]} numberOfLines={1}>
                  {`${fmt(h.listas)} listas`}
                  <Text style={{ color: C.faint }}>{` de ${fmt(h.habs)}`}</Text>
                </Text>
              </View>
              {/* Tres tramos: listo, limpiándose, sin hacer. Las bloqueadas NO
                  entran en la barra —conservan su estado de limpieza, así que
                  serían un doble recuento— y van a la derecha, en su chip. */}
              <View style={m.habBar}>
                <View style={{ width: `${Math.min(100, pl)}%`, height: '100%', backgroundColor: C.aqua }} />
                {pe > 0 ? <View style={{ width: `${Math.min(100, pe)}%`, height: '100%', backgroundColor: C.aquaSoft }} /> : null}
              </View>
              <View style={m.habPie}>
                <Text style={m.habPieL} numberOfLines={1}>
                  {h.sinCiclo ? 'socios · sin ciclo diario'
                    : `${fmt(h.vendidas ?? 0)} vendidas${h.enLimpieza ? ` · ${fmt(h.enLimpieza)} limpiándose` : ''}`}
                  {h.bloqueadas ? `  ·  ${fmt(h.bloqueadas)} bloq.${h.motivoBloqueo ? ` (${h.motivoBloqueo.motivo.toLowerCase()})` : ''}` : ''}
                </Text>
                {vsh ? <Text style={m.habPieV}>{`≥ ${fmt(vsh)} sin hacer`}</Text> : null}
              </View>
            </View>
          );
        })}

        <Text style={[m.habLbl, { marginTop: 18 }]}>BLOQUEADAS · POR QUÉ</Text>
        {bl.motivos.map((x) => (
          <View key={x.motivo} style={m.tbRow}>
            <Text style={m.tbName} numberOfLines={1}>{x.motivo}</Text>
            <Text style={m.habVal}>{fmt(x.n)}</Text>
          </View>
        ))}
        {/* 🔴 Los dos números se dicen los dos. La ocupación de Pulse cuenta las
            bloqueadas del PLANNING (cuántas descuenta hoy) y esto cuenta las que
            están bloqueadas AHORA. No coinciden, y esconder una de las dos es
            cómo se pierde la confianza en las dos pantallas a la vez. */}
        {bl.planning != null && bl.planning !== bl.vigentes ? (
          <Text style={m.footNote}>
            {`${fmt(bl.vigentes)} bloqueadas ahora mismo · el planning descuenta ${fmt(bl.planning)} hoy. `
             + 'Son dos preguntas distintas, no un error.'}
          </Text>
        ) : null}
        {dato.sinCiclo.hoteles.length ? (
          <Text style={m.footNote}>
            {`${dato.sinCiclo.hoteles.length} apart-hoteles de socios (${fmt(dato.sinCiclo.habs)} habitaciones) no tienen `
             + 'limpieza diaria: solo se limpia al entrar y salir un socio. «Sin hacer» ahí no es '
             + 'suciedad, así que no entran en el porcentaje ni llevan el número rojo.'}
          </Text>
        ) : null}
      </ZoomDetalle>
    </>
  );
}

function Incid() {
  return (
    <Card demo>
      <SectionTitle dot={C.warn}>Incidencias y objetos</SectionTitle>
      <Row icon={I('wrench')} label="Incidencias abiertas" value="17" chip="3 críticas" chipKind="bad" />
      <Row icon={I('case')} label="Objetos perdidos (nuevos)" value="5" chip="2 reclamados" />
      <Row icon={I('cal')} label="Eventos hoy en complejos" value="4" chip="2 bodas" last />
    </Card>
  );
}

/* ---------- Revenue ---------- */

function Pickup() {
  const { dato, cargando, motivo } = useLive<PickupLive>(() => api.pickup());
  if (cargando) return <SinDato cargando titulo="Pickup · últimos 7 días" motivo="Contando altas…" />;
  if (!dato || !dato.disponible) {
    return (
      <SinDato
        titulo="Pickup · últimos 7 días"
        motivo={motivo ?? dato?.nota
          ?? 'Ningún espejo de reservas trae hoy la fecha de alta, que es lo que hace falta para contar el pickup.'}
      />
    );
  }

  const dia = (iso: string) => {
    const d = new Date(iso + 'T00:00:00');
    return ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'][d.getDay()];
  };

  return (
    <Card>
      <SectionTitle right={`${dato.porPms.filter((p) => p.cubierto).length} PMS`}>
        Pickup · últimos 7 días
      </SectionTitle>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
        <View>
          <Text style={m.heroNum}>+{fmt(dato.rn ?? 0)}</Text>
          <Text style={m.subTxt}>room-nights nuevas</Text>
        </View>
        <Spark vals={dato.serie} width={120} height={44} />
      </View>
      <View style={m.chipRow}>
        {dato.mejorDia ? (
          <Chip kind="good">{`▲ mejor día: ${dia(dato.mejorDia.fecha)} +${fmt(dato.mejorDia.rn)}`}</Chip>
        ) : null}
        {dato.reservas != null ? <Chip>{`${fmt(dato.reservas)} reservas`}</Chip> : null}
        {/* Lo que NO entra se dice en la tarjeta, no en una nota al pie que nadie lee. */}
        {dato.faltan.length ? <Chip kind="warn">{`sin ${dato.faltan.join(' · ')}`}</Chip> : null}
      </View>
      <Text style={m.footNote}>{dato.nota}</Text>
    </Card>
  );
}

function Heat() {
  const { dato, cargando, motivo } = useLive<RejillaLive>(() => api.rejilla(30), 10 * 60e3);
  const [todos, setTodos] = useState(false);
  if (cargando) return <SinDato cargando titulo="Ocupación · próximos 30 días" motivo="Leyendo los 4 PMS…" />;
  if (!dato || !dato.filas.length) {
    return <SinDato titulo="Ocupación · próximos 30 días" motivo={motivo ?? 'Los espejos de ocupación no devuelven ningún hotel.'} />;
  }

  const hs = dato.filas.slice(0, 8);
  const ob = dato.filas.reduce((n, f) => n + (f.ob > 0 ? 1 : 0), 0);
  const pms = new Set(dato.filas.map((f) => f.pms)).size;

  const celda = (v: number | null, ancho = 15) => ({
    width: ancho, height: 17, borderRadius: 4, marginRight: 3,
    backgroundColor: v == null ? 'rgba(140,200,215,0.07)'
      : v > dato.umbralOverbooking ? C.bad : heatColor(v),
  });

  return (
    <>
    <Pressable onPress={() => setTodos(true)}>
    <Card>
      <SectionTitle right={`ver los ${dato.filas.length} →`}>Ocupación · próximos 30 días</SectionTitle>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View>
          {hs.map((h) => (
            <View key={h.id} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 3 }}>
              <Text style={m.heatName} numberOfLines={1}>{h.n.replace('Ona ', '')}</Text>
              {/* sin dato ≠ vacío: se pinta apagado, nunca como un 60 % */}
              {h.celdas.map((v, d) => <View key={d} style={celda(v)} />)}
            </View>
          ))}
        </View>
      </ScrollView>
      <View style={m.heatLeg}>
        <Text style={m.legTxt}>60%</Text>
        {HEAT_RAMP.map((c) => <View key={c} style={[m.heatLegCell, { backgroundColor: c }]} />)}
        <Text style={m.legTxt}>100%</Text>
        <View style={[m.heatLegCell, { backgroundColor: C.bad, marginLeft: 6 }]} />
        <Text style={m.legTxt}>overbooking</Text>
      </View>
      {ob ? (
        <Text style={m.footNote}>
          {ob} hotel{ob === 1 ? '' : 'es'} por encima del {String(dato.umbralOverbooking).replace('.', ',')}% algún día — están en Alertas.
        </Text>
      ) : (
        <Text style={m.footNote}>Ningún hotel en overbooking en los próximos 30 días.</Text>
      )}
      <Text style={m.footNote}>
        {`Se enseñan los ${hs.length} primeros de ${dato.filas.length} · toca para ver todos.`}
      </Text>
    </Card>
    </Pressable>

    {/* Todos los hoteles, con su nombre y su media, porque en una lista de 53
        la rejilla sola no dice quién es quién. */}
    <BottomSheet visible={todos} onClose={() => setTodos(false)} maxAlto="92%">
      <Text style={m.hojaT}>Ocupación · próximos 30 días</Text>
      <Text style={m.subTxt}>
        {`${dato.filas.length} hoteles · ${pms} PMS · desde ${dato.fechas[0]?.slice(8, 10)}/${dato.fechas[0]?.slice(5, 7)}`}
      </Text>
      <ScrollView style={{ marginTop: 12 }} showsVerticalScrollIndicator={false} bounces={false} overScrollMode="never">
        {dato.filas.map((h) => {
          const v = h.celdas.filter((c) => c != null) as number[];
          const media = v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
          return (
            <View key={h.id} style={m.hojaFila}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Text style={m.hojaN} numberOfLines={1}>{h.n.replace(/^Ona /i, '')}</Text>
                {h.ob ? <Chip kind="bad">{`${h.ob} d >100%`}</Chip> : null}
                <Text style={m.hojaMedia}>{media != null ? `${fmtDec(media)}%` : '—'}</Text>
              </View>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 5 }}>
                <View style={{ flexDirection: 'row' }}>
                  {h.celdas.map((c, d) => <View key={d} style={celda(c, 13)} />)}
                </View>
              </ScrollView>
            </View>
          );
        })}
      </ScrollView>
    </BottomSheet>
    </>
  );
}

function Agencias() {
  const { dato, cargando, motivo } = useLive<ComisionesLive>(() => api.comisiones(), 30 * 60e3);
  if (cargando) return <SinDato cargando titulo="Comisiones de agencias" motivo="Leyendo los acuerdos vigentes…" />;
  if (!dato) return <SinDato titulo="Comisiones de agencias" motivo={motivo ?? 'No hay acceso a los acuerdos de comisión.'} />;
  const mx = dato.lista.reduce((n, x) => Math.max(n, x.max ?? 0), 0) || 1;
  return (
    <Card>
      {/* 🔴 ESTA TARJETA CONTESTA LO QUE SE PUEDE, NO LO QUE PARECÍA. Antes pintaba
          «Booking 214.300 € · Expedia 118.200 €» — inventado. Los importes de
          comisión SOLO existen en el espejo de Avalon, que es un hotel del grupo:
          darlos como cifra de grupo sería peor que no darlos. Lo que sí es real y
          está aquí son los PORCENTAJES pactados, y eso ya contesta «¿qué estamos
          pagando y a quién?». */}
      <SectionTitle right={`${dato.acuerdos} acuerdos vigentes`}>Comisiones pactadas</SectionTitle>
      {!dato.lista.length ? (
        <Text style={m.sinDato}>No hay acuerdos vigentes hoy.</Text>
      ) : (
        <View style={{ gap: 11 }}>
          {dato.lista.slice(0, 6).map((x) => (
            <View key={x.agencia}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5, gap: 10 }}>
                <Text style={m.tbName} numberOfLines={1}>{x.agencia}</Text>
                <Text style={m.wfVal}>
                  {x.min != null && x.max != null && x.min !== x.max
                    ? `${fmtDec(x.min)} – ${fmtDec(x.max)} %`
                    : `${fmtDec(x.max ?? x.min ?? 0)} %`}
                </Text>
              </View>
              <Bar pct={((x.max ?? 0) / mx) * 100} />
              <Text style={m.subTxt}>
                {`${x.hoteles} hotel${x.hoteles === 1 ? '' : 'es'}${x.tipos.length ? ` · ${x.tipos.join(', ')}` : ''}`}
              </Text>
            </View>
          ))}
        </View>
      )}
      <Text style={m.footNote}>{dato.nota}</Text>
    </Card>
  );
}

/* ---------- Financiero ---------- */

function Cash() {
  const { dato, cargando, motivo } = useLive<CajaLive>(() => api.caja());
  if (cargando) return <SinDato cargando titulo="Caja · ayer" motivo="Sumando los cobros…" />;
  if (!dato) return <SinDato titulo="Caja · ayer" motivo={motivo ?? 'La cartera de clientes no responde.'} />;
  const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
  return (
    <Card>
      <SectionTitle right={ddmm(dato.fecha)}>Caja · ayer</SectionTitle>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
        <View>
          <Text style={m.heroNum}>{fmtE(dato.importe)}</Text>
          <Text style={m.subTxt}>cobros registrados</Text>
        </View>
        <Spark vals={dato.serie} width={100} height={42} color={C.good} />
      </View>
      <View style={m.chipRow}>
        <Chip>{`${fmt(dato.apuntes)} apuntes`}</Chip>
        <Chip kind="good">{`${fmtE(dato.mes)} en el mes`}</Chip>
      </View>
      <Text style={m.footNote}>{dato.nota}</Text>
    </Card>
  );
}

/* AGEING — la tarjeta se DA LA VUELTA (pedido de Patricio): delante el semáforo
   por tramos, detrás a quién hay que llamar. Un ageing del que no sale una
   llamada es un gráfico bonito y poco más. */
/* 🔴 DOS REVERSOS, UNO POR AUDIENCIA. El de siempre —a quién cobrar, tramo a
   tramo— es el que necesita Finanzas: cinco tramos por cinco clientes es su
   trabajo del día. Para Dirección General el acuerdo de la demo pide otra cosa:
   «quién debe pagar a 30 días y cuánto», o sea mirando hacia DELANTE. No se
   apilan las dos en la misma tarjeta con tres toques: en una demo, un tercer
   toque te deja en un sitio que no esperabas. Cada portada lleva el reverso que
   le sirve. */
function Ageing({ vista30 = false }: { vista30?: boolean }) {
  const { dato, cargando, motivo } = useLive<AgeingLive>(() => api.ageing());
  const [detras, setDetras] = useState(false);
  if (cargando) return <SinDato cargando titulo="Ageing clientes" dot={C.warn} motivo="Leyendo la cartera…" />;
  if (!dato) return <SinDato titulo="Ageing clientes" dot={C.warn} motivo={motivo ?? 'La cartera de clientes no responde.'} />;

  const venc = dato.tramos.filter((t) => t.vencido);
  const color = (i: number) => AGEING_COLORS[Math.min(i, AGEING_COLORS.length - 1)];
  /* Si el API es viejo no manda `prox30`: se cae al reverso de tramos en vez de
     enseñar una cara vacía. La app nueva con el servidor sin desplegar es un
     estado real, no una hipótesis. */
  const p30 = vista30 ? dato.prox30 : undefined;
  const cara30 = !!p30;

  return (
    <Pressable onPress={() => setDetras((v) => !v)}>
      <Card>
        <SectionTitle dot={C.warn} right={detras ? '← el resumen' : (cara30 ? 'quién paga a 30 días →' : 'a quién cobrar →')}>
          {`Ageing clientes · ${fmtM(dato.total)}`}
        </SectionTitle>

        {!detras ? (
          <>
            <SegBar segs={venc.map((t, i) => ({ v: Math.max(t.importe, 0), color: color(i) }))} />
            <View style={m.legGrid}>
              {venc.map((t, i) => (
                <View key={t.clave} style={m.legItem}>
                  <View style={[m.legDot, { backgroundColor: color(i) }]} />
                  <Text style={m.legTxt}>{t.texto} <Text style={m.legVal}>{fmtK(t.importe)}</Text></Text>
                </View>
              ))}
            </View>
            <View style={m.chipRow}>
              <Chip kind="bad">{`vencido ${fmtM(dato.vencido)}`}</Chip>
              <Chip>{`no vencido ${fmtM(dato.noVencido)}`}</Chip>
              <Chip>{`${fmt(dato.facturas)} facturas`}</Chip>
            </View>
            <Text style={m.footNote}>
              {cara30
                ? `Toca la tarjeta para ver quién tiene que pagar en los próximos 30 días.`
                : 'Toca la tarjeta para ver a quién hay que cobrar en cada tramo.'}
            </Text>
          </>
        ) : p30 ? (
          <>
            <Text style={m.tramoT}>{`Cobros previstos · próximos 30 días`}</Text>
            <Text style={m.subTxt}>
              {`Aún no vencido. ${fmtM(p30.total)} hasta el ${fmtDiaMes(p30.hasta)}.`}
            </Text>
            <View style={{ marginTop: 10 }}>
              {p30.top.slice(0, 6).map((c, i) => (
                /* La clave lleva el índice: agrupando por `customer_no`, dos
                   clientes distintos pueden resolverse al mismo nombre, y el
                   nombre solo daría claves repetidas. */
                <View key={`${c.n}|${i}`} style={m.cliRow}>
                  <Text style={m.cliN} numberOfLines={1}>{c.n}</Text>
                  <Text style={m.cliV}>{fmtE(c.imp)}</Text>
                  <Text style={m.cliF}>{fmtDiaMes(c.vence)}</Text>
                </View>
              ))}
            </View>
            {/* 🔴 EL RECUENTO DICE CUÁNTOS HAY, NO CUÁNTOS SE VEN. Se enseñan seis
                para que la tarjeta no se estire, y el pie declara el total de
                clientes y de euros: un recorte que no se dice se lee como «esto
                es todo». */}
            <View style={{ backgroundColor: C.card2, borderRadius: 10, padding: 10, marginTop: 10 }}>
              <Text style={{ fontFamily: F.uiMed, fontSize: 12.5, color: C.ink }}>
                {`${fmt(p30.clientes)} cliente${p30.clientes === 1 ? '' : 's'} · ${fmtE(p30.total)} hasta el ${fmtDiaMes(p30.hasta)}`}
              </Text>
            </View>
            {Math.abs(p30.neto - p30.total) >= 1 ? (
              <Text style={m.footNote}>
                {`Hay ${fmtE(p30.total - p30.neto)} en cobros a cuenta dentro del tramo, así que lo que entra de neto son ${fmtE(p30.neto)}.`}
              </Text>
            ) : null}
            <Text style={m.footNote}>{dato.nota}</Text>
          </>
        ) : (
          <>
            {venc.filter((t) => t.top.length).map((t, i) => (
              <View key={t.clave} style={{ marginTop: i ? 12 : 4 }}>
                <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
                  <View style={[m.legDot, { backgroundColor: color(i) }]} />
                  <Text style={m.tramoT}>{t.texto}</Text>
                  <Text style={m.tramoV}>{fmtK(t.importe)}</Text>
                </View>
                {t.top.map((c, j) => (
                  <View key={`${c.n}|${j}`} style={m.cliRow}>
                    <Text style={m.cliN} numberOfLines={1}>{c.n}</Text>
                    <Text style={m.cliV}>{fmtE(c.imp)}</Text>
                  </View>
                ))}
              </View>
            ))}
            <Text style={m.footNote}>{dato.nota}</Text>
          </>
        )}
      </Card>
    </Pressable>
  );
}

function Cierres() {
  return (
    <Card demo>
      <SectionTitle>Cierre contable · julio</SectionTitle>
      <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <Text style={[m.heroNum, { fontSize: 24 }]}>{G.cierres.ok}/{G.cierres.tot}</Text>
        <Text style={m.subTxt}>sociedades cerradas</Text>
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7 }}>
        {Array.from({ length: G.cierres.tot }, (_, i) => (
          <View key={i} style={[m.cierreDot, { backgroundColor: i >= G.cierres.ok ? 'rgba(245,184,75,0.9)' : C.good }]} />
        ))}
      </View>
      <Text style={[m.footNote, { color: C.warn }]}>Pendientes: {G.cierres.pend.join(' · ')}</Text>
    </Card>
  );
}

function Club() {
  return (
    <Card demo>
      <SectionTitle>Club Ona · cuotas</SectionTitle>
      <Row icon={I('card')} label="Cobrado YTD" value={fmtM(G.club.ytd)} chip={`${G.club.plan}% del plan`} chipKind="good" />
      <Row icon={I('mail')} label="Socios en monitorio" value={fmt(G.club.morosos)} chip={`▼ ${Math.abs(G.club.dMor)} este mes`} chipKind="good" last />
      <Text style={m.footNote}>Cada cobro en app = un monitorio menos (fase 3)</Text>
    </Card>
  );
}

/* ---------- Sistemas ---------- */

/* 🔴 LA PORTADA DE SISTEMAS CONTESTA OTRA PREGUNTA. Patricio, 27-08: «a sistemas
   lo que factura un hotel no es tan relevante». Para dirección la portada dice
   «¿cómo vamos?»; aquí dice «¿HAY ALGO ROTO O A PUNTO DE ROMPERSE, Y ME VAN A
   LLAMAR?». Cuatro números, en el orden en que duelen.

   Las cuatro reglas que los hacen útiles y no bonitos:
     1. NOMBRAR AL CULPABLE, no solo contar: «7 pendientes» no dice qué hacer;
        «Palmiras, sin cerrar desde el 25» sí. Por eso cada uno se abre.
     2. Los saldos, en DÍAS y con fecha — 47 $ no es un KPI, «12 días» sí.
     3. En tickets, TRES números: los abiertos son ruido; lo que cuesta una
        llamada son los sin asignar y los fuera de SLA.
     4. Y un KPI sin umbral ni dueño es decoración, que en una portada compite
        con lo que sí importa. Lo que no cumple eso se quedó fuera (inventario,
        telefonía, contraseñas): están en el roadmap §8 con el motivo. */
function SisKpis() {
  const { dato, cargando, motivo } = useLive<SistemasKpis>(() => api.sistemasKpis(), 5 * 60e3);
  if (cargando) return <SinDato cargando titulo="Sistemas, ahora" motivo="Preguntando por cierres, tickets e incidencias…" />;
  if (!dato) return <SinDato titulo="Sistemas, ahora" motivo={motivo ?? 'No hay acceso a los registros de Hotelos.'} />;

  const { cierres, tickets, incidencias, salud, saldo } = dato;
  const hhmm = (t?: string | null) => (t ? new Date(t).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
  const horas = (t?: string | null) => (t ? Math.floor((Date.now() - new Date(t).getTime()) / 3600e3) : null);
  const dias = (t?: string | null) => (t ? Math.floor((Date.now() - new Date(t).getTime()) / 86400e3) : null);
  /* 🔴 CORTAR POR PALABRA, NO POR LETRA: partiendo a 46 caracteres salió en
     pantalla «Sync de reconciliación de movimientos con fall», y un texto roto a
     media palabra parece un fallo del programa. */
  const corta = (t: string, n = 40) => {
    const x = String(t || '').trim();
    if (x.length <= n) return x;
    const c = x.slice(0, n);
    const e = c.lastIndexOf(' ');
    return `${(e > n * 0.6 ? c.slice(0, e) : c).trim()}…`;
  };

  const pend = cierres?.pendientes ?? [];
  const peorServicio = salud?.servicios?.find((x) => x.ms != null) ?? null;
  const incAgrup = (() => {
    const mm = new Map<string, { n: number; sev: string | null; serv: string | null }>();
    (incidencias?.lista ?? []).forEach((x) => {
      const k = x.titulo || '—';
      const y = mm.get(k);
      if (y) y.n += 1; else mm.set(k, { n: 1, sev: x.sev, serv: x.servicio });
    });
    return [...mm.entries()];
  })();
  /* El saldo, en fecha: «12 días» dice cuánto queda, pero un día del calendario es
     lo que se apunta en la agenda para recargar antes de que pare. */
  const seAgota = saldo?.diasRestantes != null
    ? new Date(Date.now() + saldo.diasRestantes * 86400e3)
      .toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })
    : null;

  return (
    <>
      {/* 🔴 ANCHO COMPLETO, NO CUATRO COLUMNAS (30-08). Se probaron como tarjetas
          pequeñas 2×2 y Patricio lo dijo con la captura delante: «¿no quedaría
          mejor como lo planteaste al principio?». Tenía razón — con un cuarto del
          ancho no cabe LA LÍNEA QUE NOMBRA AL CULPABLE, y esa línea es justo lo
          que convierte un contador en algo accionable. El gesto de abrir girando
          se conserva con `AbreKpi`. */}
      {cierres ? (
        <Card>
          <AbreKpi
            value={cierres.ok != null ? String(cierres.ok) : String(cierres.total ?? '—')}
            label="cierres del día"
            detail={{
              rows: (pend.length
                ? pend.map((x) => [corta(x.hotel, 28), x.error ? corta(x.error, 20) : `${x.lineas} líneas${x.desde ? ` · ${x.desde.slice(5)}` : ''}`])
                : [['Sin líneas pendientes', 'todo cerrado']]) as Array<[string, string]>,
              foot: `${cierres.errores ? `${cierres.errores} con error · ` : ''}revisado ${hhmm(cierres.revisado)}. Estados: ${(cierres.porEstado ?? []).map((x) => `${x.n} ${x.estado}`).join(' · ')}.`,
            }}>
            <SectionTitle right={`${cierres.total ?? '—'} hoteles`}>Cierres del día</SectionTitle>
            <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
              <Text style={m.sisNum}>{cierres.ok ?? '—'}</Text>
              <Text style={m.sisUnit}>{`cerrados${pend.length ? ` · ${pend.length} con líneas` : ''}`}</Text>
            </View>
            <View style={m.sisBarra}>
              <View style={{ width: `${Math.min(100, (100 * (cierres.ok ?? 0)) / (cierres.total || 1))}%`, height: '100%', backgroundColor: C.aqua }} />
            </View>
            <Text style={[m.sisLinea, { color: pend.length ? C.warn : C.muted }]}>
              {pend.length
                ? `El que más pesa: ${corta(pend[0].hotel, 26)}${pend[0].error ? ` · ${corta(pend[0].error, 22)}` : ` · ${pend[0].lineas} líneas${pend[0].desde ? ` desde el ${pend[0].desde.slice(8)}` : ''}`}`
                : 'Ningún hotel con líneas pendientes.'}
            </Text>
          </AbreKpi>
        </Card>
      ) : null}

      {tickets ? (
        <Card>
          <AbreKpi value={String(tickets.abiertos)} label="tickets abiertos"
            detail={{
              rows: (tickets.sinDueno.length
                ? tickets.sinDueno.map((x) => [corta(x.asunto || `#${x.id}`, 34), `${dias(x.desde) ?? '—'} d${x.hotel ? ` · ${corta(x.hotel, 12)}` : ''}`])
                : [['Ninguno sin asignar', 'todos tienen dueño']]) as Array<[string, string]>,
              foot: `Los ${tickets.abiertos} abiertos son ruido: lo que cuesta una llamada son los ${tickets.sinAsignar} que nadie ha mirado. Sincronizado ${hhmm(tickets.sync)}.`,
            }}>
            <SectionTitle right={tickets.guardia ? `${tickets.guardia} de guardia` : `sync ${hhmm(tickets.sync).slice(-5)}`}>Tickets</SectionTitle>
            {/* Tres números y no uno: los abiertos son ruido; lo que cuesta una
                llamada son los que nadie ha mirado y los que se han pasado. */}
            <View style={{ flexDirection: 'row', gap: 22 }}>
              {([['abiertos', tickets.abiertos, C.ink],
                 ['sin asignar', tickets.sinAsignar, tickets.sinAsignar ? C.warn : C.muted],
                 ['fuera de SLA', tickets.fueraSla, tickets.fueraSla ? C.bad : C.muted]] as Array<[string, number, string]>)
                .map(([l, v, col]) => (
                  <View key={l}>
                    <Text style={[m.sisNum, { color: col }]}>{v}</Text>
                    <Text style={m.sisPie}>{l}</Text>
                  </View>
                ))}
            </View>
            <Text style={[m.sisLinea, { color: tickets.sinAsignar ? C.bad : C.muted }]}>
              {tickets.sinAsignar
                ? `El más viejo sin asignar lleva ${horas(tickets.masViejo) ?? '—'} h`
                : 'Todos los abiertos tienen dueño.'}
            </Text>
          </AbreKpi>
        </Card>
      ) : null}

      {incidencias ? (
        <Card>
          <AbreKpi value={String(incidencias.abiertas)} label="incidencias abiertas"
            detail={{
              rows: (incAgrup.length
                ? incAgrup.map(([t, v]) => [`${corta(t, 34)}${v.n > 1 ? ` ×${v.n}` : ''}`, `${(v.sev ?? '—').toLowerCase()}${v.serv ? ` · ${corta(v.serv, 10)}` : ''}`])
                : [['Ninguna abierta', 'nada roto ahora mismo']]) as Array<[string, string]>,
              foot: `${incidencias.resueltasAuto7d} se resolvieron solas en 7 días. Si son muchas, el ruido automático está tapando las de verdad.`,
            }}>
            <SectionTitle right={(incidencias.porSeveridad ?? []).map((x) => `${x.n} ${String(x.sev).toLowerCase()}`).join(' · ') || 'ninguna'}>
              Incidencias abiertas
            </SectionTitle>
            <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
              <Text style={[m.sisNum, { color: incidencias.abiertas ? C.bad : C.ink }]}>{incidencias.abiertas}</Text>
              <Text style={m.sisUnit}>{incidencias.abiertas === 1 ? 'sin resolver' : 'sin resolver'}</Text>
            </View>
            {incAgrup.slice(0, 2).map(([t, v]) => (
              <Text key={t} style={[m.sisLinea, { color: C.warn }]} numberOfLines={2}>
                {`${corta(t, 44)}${v.n > 1 ? ` ×${v.n}` : ''}${v.serv ? ` · ${v.serv}` : ''}`}
              </Text>
            ))}
            {!incAgrup.length ? <Text style={[m.sisLinea, { color: C.muted }]}>Nada roto ahora mismo.</Text> : null}
          </AbreKpi>
        </Card>
      ) : null}

      {saldo ? (
        <Card>
          <AbreKpi value={saldo.diasRestantes != null ? `${saldo.diasRestantes}` : `${Math.round(saldo.saldoUsd)} $`} label="saldo API"
            detail={{
              rows: [
                ['Saldo', `${saldo.saldoUsd.toFixed(2)} $`],
                ['Crédito', `${saldo.creditoUsd.toFixed(2)} $`],
                ['Gasto por día', saldo.porDia ? `${saldo.porDia} $` : 'no se deduce'],
                ['Comprobado', hhmm(saldo.revisado)],
              ] as Array<[string, string]>,
              foot: saldo.obsoleto
                ? `🔴 El comprobador lleva ${saldo.horasDesde} h sin correr. El número de arriba es viejo: el saldo puede agotarse sin que esta tarjeta se entere.`
                : 'Un saldo en dólares no avisa de nada; en días sí, porque tiene acantilado.',
            }}>
            <SectionTitle right={`${saldo.saldoUsd.toFixed(2)} $`}>Saldo API Anthropic</SectionTitle>
            {saldo.obsoleto ? (
              <>
                <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
                  <Text style={[m.sisNum, { color: C.bad }]}>{Math.round(saldo.horasDesde / 24)}</Text>
                  <Text style={m.sisUnit}>días sin comprobarse</Text>
                </View>
                <Text style={[m.sisLinea, { color: C.bad }]}>
                  {`El comprobador dejó de correr el ${hhmm(saldo.revisado)}. El saldo puede agotarse sin que nadie se entere.`}
                </Text>
              </>
            ) : (
              <>
                <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
                  <Text style={[m.sisNum, { color: (saldo.diasRestantes ?? 99) < 15 ? C.warn : C.ink }]}>{saldo.diasRestantes ?? '—'}</Text>
                  <Text style={m.sisUnit}>días a este ritmo</Text>
                </View>
                <Text style={[m.sisLinea, { color: C.muted }]}>
                  {`${saldo.porDia ?? '—'} $/día de media${seAgota ? ` · se agota el ${seAgota}` : ''}`}
                </Text>
              </>
            )}
          </AbreKpi>
        </Card>
      ) : null}

      {salud?.servicios?.length ? (
        <Card>
          <SectionTitle right={`${salud.servicios.length} servicios`}>Salud de los servicios</SectionTitle>
          <Text style={m.sisLinea}>
            {salud.porEstado.map((x) => `${x.n} ${x.estado}`).join(' · ')}
            {peorServicio?.ms != null ? ` · el más lento: ${peorServicio.nombre} (${peorServicio.ms} ms)` : ''}
          </Text>
          <Text style={m.footNote}>
            La latencia es lo que avisa ANTES de que algo se caiga: un servicio que hoy tarda 3 s es el que mañana estará caído.
          </Text>
        </Card>
      ) : null}
    </>
  );
}

function PmsHealth() {
  const { dato, cargando, motivo } = useLive<SistemasLive>(() => api.sistemas(), 5 * 60e3);
  // 🔴 «2 fallos» y ahí se acababa la información: había que ir a Hotelos a
  // averiguar cuáles. Ahora la celda se toca y cuenta qué falló y cuándo.
  const [abierto, setAbierto] = useState<string | null>(null);
  if (cargando) return <SinDato cargando titulo="Latido de los 4 PMS" motivo="Preguntando a las sincronizaciones…" />;
  if (!dato) {
    return <SinDato titulo="Latido de los 4 PMS" motivo={motivo ?? 'No hay acceso al registro de sincronizaciones de Hotelos.'} />;
  }
  const color = (e: string) => (e === 'bad' ? C.bad : e === 'warn' ? C.warn : C.good);
  const sel = dato.pms.find((p) => p.pms === abierto) ?? null;

  return (
    <>
      <Card>
        <SectionTitle right="toca para el detalle">Latido de los 4 PMS</SectionTitle>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
          {dato.pms.map((p) => (
            <Pressable key={p.pms} style={m.pmsCell} onPress={() => setAbierto(p.pms)}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <StatusDot color={color(p.estado)} size={9} />
                <Text style={m.pmsName}>{p.pms}</Text>
              </View>
              <Text style={m.pmsMsg}>{p.msg}</Text>
            </Pressable>
          ))}
        </View>
        <Text style={m.footNote}>{dato.nota}</Text>
      </Card>

      <BottomSheet visible={sel != null} onClose={() => setAbierto(null)} maxAlto="88%">
        {sel ? (
          <ScrollView showsVerticalScrollIndicator={false} bounces={false} overScrollMode="never">
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <StatusDot color={color(sel.estado)} size={11} />
              <Text style={m.hojaT}>{sel.pms}</Text>
            </View>
            <Text style={m.subTxt}>
              {sel.hoteles ? `${sel.hoteles} hotel${sel.hoteles === 1 ? '' : 'es'} en la rejilla` : 'sin hoteles en la rejilla'}
              {sel.ultima ? ` · última corrida ${new Date(sel.ultima).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
            </Text>

            {!sel.detalle?.length ? (
              <Text style={[m.sinDato, { marginTop: 14 }]}>
                {sel.silencioso
                  ? 'Este PMS solo deja constancia cuando algo falla, y no hay ninguna en 7 días. Silencio es buena noticia.'
                  : 'No hay corridas registradas de este PMS en los últimos 7 días.'}
              </Text>
            ) : (
              <View style={{ marginTop: 14 }}>
                {sel.detalle.map((f) => (
                  <View key={f.tipo} style={m.hojaFila}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <StatusDot color={f.ok ? C.good : C.bad} size={8} />
                      <Text style={m.hojaN} numberOfLines={2}>{f.que}</Text>
                      <Text style={m.hojaMedia}>{f.cuando ?? '—'}</Text>
                    </View>
                    {/* 🔴 De qué hora hablamos. Antes salía la del último PARTE
                        y los syncs de cada 10 min solo dejan parte cuando
                        fallan: «Ocupación Noray · 20 ago» se leía como «no se
                        sincroniza desde el 20» cuando había corrido hace tres
                        minutos. Ahora se dice si es la hora del espejo o la de
                        una incidencia. */}
                    <Text style={m.subTxt}>
                      {f.esFrescura ? 'último dato en el espejo' : 'último parte registrado'}
                      {f.silencioso ? ' · solo deja parte si falla' : ''}
                      {f.incidencia ? ` · última incidencia ${f.incidencia}` : ''}
                      {f.origen ? ` · ${f.origen}` : ''}
                    </Text>
                    {f.error ? <Text style={m.errTxt}>{f.error}</Text> : null}
                    {f.ok && (f.nuevos || f.actualizados) ? (
                      <Text style={m.subTxt}>
                        {`${fmt(f.nuevos ?? 0)} nuevos · ${fmt(f.actualizados ?? 0)} actualizados`}
                      </Text>
                    ) : null}
                  </View>
                ))}
              </View>
            )}
            <Text style={m.footNote}>
              Se relanza desde Hotelos › Sistemas › Sincronizaciones, con el botón «Sincronizar ahora».
            </Text>
          </ScrollView>
        ) : null}
      </BottomSheet>
    </>
  );
}

function Syncs() {
  const { dato, cargando, motivo } = useLive<SistemasLive>(() => api.sistemas(), 5 * 60e3);
  if (cargando) return <SinDato cargando titulo="Sincronizaciones" motivo="Leyendo las últimas 24 h…" />;
  if (!dato) return <SinDato titulo="Sincronizaciones" motivo={motivo ?? 'El registro de sincronizaciones no responde.'} />;

  const { syncs, cola } = dato;
  return (
    <Card>
      <SectionTitle right={`últimas ${syncs.ventana}`}>Sincronizaciones</SectionTitle>
      <Row icon={<StatusDot color={C.good} />} label="En verde" value={fmt(syncs.verde)} />
      {/* Antes el chip enseñaba los CÓDIGOS (MOV_ELIMINADOS · ONASYS_OCUPACION)
          truncados a mitad. Ahora nombres legibles, y debajo qué pasó. */}
      <Row
        icon={<StatusDot color={syncs.fallos ? C.bad : C.muted} />}
        label="Con fallos"
        value={fmt(syncs.fallos)}
        chip={syncs.fallos ? `${syncs.fallos} de ${syncs.total}` : 'ninguno'}
        chipKind={syncs.fallos ? 'bad' : 'good'}
      />
      {(syncs.fallosDetalle ?? []).map((f) => (
        <View key={f.tipo} style={m.falloFila}>
          <Text style={m.falloQue} numberOfLines={1}>{f.que}</Text>
          <Text style={m.errTxt} numberOfLines={2}>
            {f.error}{f.cuando ? ` · ${f.cuando}` : ''}
          </Text>
        </View>
      ))}
      {cola ? (
        <Row
          icon={I('clock')}
          label="Cola de carga Noray"
          value={fmt(cola.enError)}
          chip={cola.enError ? `${cola.rojos} hotel(es) en rojo` : 'despejada'}
          chipKind={cola.enError ? 'bad' : 'good'}
        />
      ) : null}
      <Row
        icon={I('sync')}
        label="Último chequeo de la cola"
        value={cola?.revisado ?? '—'}
        chip={`${cola?.hoteles ?? 0} hoteles`}
        last
      />
      {dato.sinMapear.length ? (
        <Text style={m.footNote}>
          Tipos de sync que Pulse todavía no sabe a qué PMS pertenecen: {dato.sinMapear.join(', ')}.
        </Text>
      ) : null}
    </Card>
  );
}

/* NOA APRENDE — el cuaderno y su respuesta, en Sistemas.
   🔴 Esto estuvo un rato dentro del chat: en cada «no lo sé» salía un botón
   para enseñarle. Patricio lo cortó, y con razón: improvisar una respuesta en
   caliente no es lo mismo que revisar lo que de verdad se pregunta. Aquí se ve
   la lista entera, con cuántas veces lo han preguntado y cuánta gente — que es
   lo que dice qué merece una respuesta y qué es una pregunta suelta. */
interface NoaGrupo { ejemplo: string; veces: number; sinRespuesta: number; personas: number }

function NoaAprende() {
  // Se recarga sola en cuanto se enseña algo, para que lo resuelto desaparezca
  // de la lista sin tener que salir y volver a entrar.
  const [recarga, setRecarga] = useState(0);
  const [ensenar, setEnsenar] = useState<string | null>(null);
  const [repaso, setRepaso] = useState(false);
  const { dato: d, cargando, motivo } = useLive<{
    total: number; sinRespuesta: number; grupos: NoaGrupo[];
  }>(() => api.noaPreguntas(true), 10 * 60e3, recarga);

  if (cargando) return <SinDato cargando titulo="Noa aprende" motivo="Leyendo el cuaderno…" />;
  if (!d) return <SinDato titulo="Noa aprende" motivo={motivo ?? 'El cuaderno de Noa no responde.'} />;

  const pendientes: NoaGrupo[] = (d.grupos ?? []).filter((g) => g.sinRespuesta > 0).slice(0, 6);

  return (
    <>
      <Card>
        <SectionTitle dot={pendientes.length ? C.warn : C.good} right="últimos 30 días">
          Noa aprende
        </SectionTitle>

        {!pendientes.length ? (
          <Text style={m.sinDato}>
            {d.total
              ? 'Noa ha sabido contestar a todo lo que le han preguntado este mes.'
              : 'Todavía nadie le ha preguntado nada este mes.'}
          </Text>
        ) : (
          <>
            <Text style={m.subTxt}>
              {`${d.sinRespuesta} pregunta${d.sinRespuesta === 1 ? '' : 's'} sin respuesta. `}
              Toca una para decirle qué contestar.
            </Text>
            {pendientes.map((g, i) => (
              <Pressable
                key={g.ejemplo}
                style={[m.tbRow, i === pendientes.length - 1 && { borderBottomWidth: 0 }]}
                onPress={() => setEnsenar(g.ejemplo)}
              >
                <View style={{ flex: 1 }}>
                  <Text style={m.tbName} numberOfLines={2}>{g.ejemplo}</Text>
                  <Text style={m.subTxt}>
                    {g.veces === 1 ? 'una vez' : `${g.veces} veces`}
                    {g.personas > 1 ? ` · ${g.personas} personas` : ''}
                  </Text>
                </View>
                <Text style={m.ensenaCta}>enseñar →</Text>
              </Pressable>
            ))}
          </>
        )}

        <Pressable onPress={() => setRepaso(true)} style={{ paddingTop: 12 }}>
          <Text style={m.verEnsenado}>Ver y editar lo que ya le hemos enseñado →</Text>
        </Pressable>
        <Text style={m.footNote}>
          Las respuestas que se escriban aquí las dirá Noa a quien pregunte algo parecido, y
          siempre DESPUÉS de lo que calcula: nunca taparán una cifra del día.
        </Text>
      </Card>

      <EnsenarNoa
        visible={ensenar != null} pregunta={ensenar ?? ''}
        onClose={() => setEnsenar(null)} onGuardado={() => setRecarga((x) => x + 1)}
      />
      <EnsenarNoa visible={repaso} pregunta="" modo="repaso" onClose={() => setRepaso(false)} />
    </>
  );
}


/* ══════════════════════════════════════════════════════════════════════════
   CUADRO DE MANDOS COMERCIAL — «¿voy a llegar al presupuesto?»

   Criterio de Patricio: los directores son jefes de venta. Así que el orden es
   el de una decisión, no el de un panel: cómo voy HOY → voy a llegar a fin de
   MES → dónde se me va el objetivo → qué pasa en cada outlet.

   🔴 Todo lo que se enseña aquí está cotejado contra Hotelos. El Pool Bar de
   Bahía Blanca da 20.365 / 21.574 / −1.209 / 20.239, los mismos cuatro números
   que la pantalla de Cuenta de explotación Outlets. Si algún día dejan de
   coincidir, manda Hotelos. */
/* `soloOutlets` separa los dos segmentos de Hoteles sin duplicar la tarjeta:
   en «Producción» se ve el presupuesto del hotel y se ocultan los outlets; en
   «Outlets» al revés. Misma máquina, dos vistas. */
export function Comercial({ soloOutlets = false }: { soloOutlets?: boolean }) {
  const { dato, cargando, motivo } = useLive<import('@/api/client').ComercialLive>(
    () => api.comercial(), 10 * 60e3);

  if (cargando) return <SinDato cargando titulo="Tu presupuesto" motivo="Calculando…" />;
  if (!dato || !dato.hoteles.length) {
    return <SinDato titulo="Tu presupuesto" motivo={motivo ?? 'Sin hoteles en tu alcance.'} />;
  }
  const conOutlets = soloOutlets ? dato.hoteles.filter((h) => h.outlets.length) : dato.hoteles;
  if (soloOutlets && !conOutlets.length) {
    return <SinDato titulo="Outlets" motivo="Ninguno de tus hoteles tiene outlets con actividad F&B." />;
  }
  return <>{conOutlets.map((h) => (
    <FichaComercial key={h.id} h={h} varios={conOutlets.length > 1} soloOutlets={soloOutlets} />
  ))}</>;
}

function FichaComercial({ h, varios, soloOutlets = false }: { h: import('@/api/client').ComercialHotel; varios: boolean; soloOutlets?: boolean }) {
  const mes = h.mes;
  const vaBien = mes ? mes.pctReal >= mes.pctMes : true;

  return (
    <Card>
      {/* 🔴 CADA TARJETA DICE DE QUÉ ES. «Las slide de producción y de outlets
          están todas juntas y sin título, por lo que no se entiende nada»
          —Patricio—. El nombre del hotel solo no basta: hace falta saber si lo
          que miras es su presupuesto o sus puntos de venta. */}
      <SectionTitle dot={vaBien ? C.good : C.warn} right={soloOutlets ? `${h.outlets.length} outlets` : (varios ? '' : h.patron)}>
        {`${h.nombre}${soloOutlets ? ' · outlets' : ' · presupuesto'}`}
      </SectionTitle>

      {soloOutlets ? null : (<>
      {/* HOY. Los hoteles que no están en Noray no mandan facturación durante el
          día: se les enseña el último día cerrado y se DICE, en vez de un hueco. */}
      {/* 🔴 «0 €» NO ES LO MISMO QUE «NO HAY DATO». Patricio lo dijo dos veces:
          «se siguen viendo hoteles a 0 euro». El servidor mandaba el hotel con
          total 0 y sin hora, y yo pintaba un 0 € gigante con «−100% sobre el
          día» al lado — que en una portada se lee como catástrofe cuando lo que
          pasa es que ese hotel no manda facturación mientras el día corre.
          Si no hay hora, no hay dato: se dice, y no se compara con nada. */}
      {(() => { const sinDato = !h.hoy.cerrado && !h.hoy.hasta; return (<>
      <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 10, marginTop: 2 }}>
        <Text style={m.heroNum}>{sinDato ? 'Sin dato hoy' : (h.hoy.importe != null ? fmtE(h.hoy.importe) : '—')}</Text>
        {h.objetivoDia != null && h.hoy.importe != null && !h.hoy.cerrado && !sinDato ? (
          <Chip kind={h.hoy.importe >= h.objetivoDia ? 'good' : 'bad'}>
            {`${h.hoy.importe >= h.objetivoDia ? '+' : ''}${Math.round((100 * h.hoy.importe) / h.objetivoDia - 100)}% sobre el día`}
          </Chip>
        ) : null}
      </View>
      <Text style={m.subTxt}>
        {h.hoy.cerrado
          ? `Último día cerrado (${h.hoy.fecha}) — este hotel no manda facturación durante el día`
          : sinDato
            ? 'Este hotel no manda facturación mientras el día corre'
            : `Hoy hasta las ${h.hoy.hasta} · provisional`}
        {h.objetivoDia != null && !sinDato ? ` · objetivo ${fmtE(h.objetivoDia)}` : ''}
      </Text>
      </>); })()}

      {/* EL MES. La línea que convierte el presupuesto en algo accionable un
          martes por la tarde: cuánto falta, en cuántos días, a cuánto por día. */}
      {mes ? (
        <>
          <View style={{ height: 7, borderRadius: 99, backgroundColor: C.card2, overflow: 'hidden', marginTop: 14 }}>
            <View style={{ width: `${Math.min(100, mes.pctReal)}%`, height: '100%', backgroundColor: vaBien ? C.good : C.warn }} />
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 5 }}>
            <Text style={m.legTxt}>{`${fmtE(mes.conseguido)} de ${fmtE(mes.presupuesto)}`}</Text>
            <Text style={m.legTxt}>{`${mes.pctReal}% con el ${mes.pctMes}% del mes`}</Text>
          </View>
          {mes.faltan != null && mes.porDia != null ? (
            <View style={{ backgroundColor: C.card2, borderRadius: 10, padding: 10, marginTop: 10 }}>
              <Text style={{ fontFamily: F.uiMed, fontSize: 12.5, color: C.ink }}>
                {`Te faltan ${fmtE(mes.faltan)} en ${mes.diasRestantes} día${mes.diasRestantes === 1 ? '' : 's'}`}
              </Text>
              <Text style={m.subTxt}>
                {`${fmtE(mes.porDia)} al día`}{mes.ayer != null ? ` · ayer hiciste ${fmtE(mes.ayer)}` : ''}
              </Text>
            </View>
          ) : null}
        </>
      ) : (
        <Text style={[m.subTxt, { marginTop: 10 }]}>
          Este hotel no tiene presupuesto cargado, así que no hay contra qué comparar.
        </Text>
      )}

      {/* DÓNDE SE TE VA EL OBJETIVO. Volumen y precio suman exacto la brecha de
          alojamiento, así que no queda un residuo que nadie sabe explicar. */}
      {h.brecha.length ? (
        <View style={{ marginTop: 14 }}>
          <Text style={m.legTxt}>Dónde se te va el objetivo</Text>
          {h.brecha.map((b) => (
            <View key={b.causa} style={m.miniRow}>
              <Text style={m.miniLbl}>{b.que}</Text>
              <Text style={[m.miniVal, { color: b.imp < 0 ? C.bad : C.good }]}>
                {`${b.imp > 0 ? '+' : ''}${fmtE(b.imp)}`}
              </Text>
            </View>
          ))}
        </View>
      ) : null}

      {/* LAS PALANCAS. Con el presupuesto de P&L no hay noches ni precio medio
          presupuestados, así que se dice en vez de enseñar guiones mudos. */}
      {h.palancas && h.palancas.ocBud != null ? (
        <View style={{ marginTop: 12 }}>
          <View style={m.miniRow}>
            <Text style={m.miniLbl}>Ocupación</Text>
            <Text style={m.miniVal}>{`${h.palancas.ocReal ?? '—'}% vs ${h.palancas.ocBud}% ppto`}</Text>
          </View>
          <View style={m.miniRow}>
            <Text style={m.miniLbl}>Precio medio</Text>
            <Text style={m.miniVal}>{`${h.palancas.arrReal ?? '—'} € vs ${h.palancas.arrBud} € ppto`}</Text>
          </View>
        </View>
      ) : null}

      </>)}

      {/* OUTLETS. Todos los del hotel, el que más cuesta arriba. */}
      {h.outlets.length ? (
        <View style={{ marginTop: 14 }}>
          <Text style={m.legTxt}>{`Outlets · ${h.outlets.length}`}</Text>
          {(() => { const veces = new Map<string, number>();
            for (const o of h.outlets) veces.set(o.nombre, (veces.get(o.nombre) || 0) + 1);
            return h.outlets.slice(0, 8); })().map((o) => {
            /* 🔴 «MAR MENOR- TERRAZA AZUL» dentro de una tarjeta que ya se titula
               «Ona Mar Menor» gasta media línea en repetir el hotel, y luego el
               nombre del outlet se corta. Se recorta el prefijo del hotel
               comparando palabra a palabra: así vale para «MAR MENOR-» y para
               «BAHIA BLANCA-» sin una lista de casos. */
            const limpio = (() => {
              const norm = (t: string) => t.toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
              const hotelP = norm(h.nombre).split(' ').filter((w) => w !== 'ONA' && w.length > 2);
              let out = o.nombre;
              /* 🔴 PREFIJOS QUE NO SON EL NOMBRE DEL HOTEL. «PI- BAR PISCINA» en
                 Ona Cala Pi, «N/A- PALMIRA PARADISE- LOBBY BAR» en Palmiras: el
                 primero es una abreviatura del hotel y el segundo la marca de
                 dimensión sin asignar. Mi recorte solo quitaba palabras del
                 nombre del hotel, así que ninguno de los dos caía. Se quita
                 cualquier prefijo corto seguido de guion —y el «N/A» siempre—,
                 que es lo que de verdad estorba para leer el outlet. */
              out = out.replace(/^\s*N\/?A\s*[-—·]\s*/i, '');
              out = out.replace(/^\s*[A-Za-zÁÉÍÓÚÑ0-9]{1,4}\s*[-—·]\s+/, '');
              for (const w of hotelP) {
                const re = new RegExp(`^\\s*${w}\\s*[-—·]?\\s*`, 'i');
                if (re.test(norm(out)) || re.test(out)) out = out.replace(re, '');
              }
              return out.replace(/^[-—·\s]+/, '').trim() || o.nombre;
            })();
            /* 🔴 UNA BARRA POR OUTLET — pedido de Patricio: «debajo de cada uno
               pondría una barra para que sepan cuánto le falta para llegar al
               presupuesto». Y con el presupuesto A LA VISTA, no solo la brecha:
               «−0,12 M€» no dice si eso es mucho o poco; «52.300 de 172.000»
               sí. El ancho se acota al 100 % para que pasarse no rompa la fila,
               y cuando pasa se pinta en verde, que es lo que significa. */
            const pctO = o.real != null && o.presupuesto ? (100 * o.real) / o.presupuesto : null;
            return (
            <View key={o.dim} style={{ paddingVertical: 5 }}>
              <View style={m.miniRow}>
                <Text style={m.miniLbl} numberOfLines={1}>{limpio}</Text>
                <Text style={m.miniVal}>
                  {o.real != null ? fmtE(o.real) : '—'}
                  {o.presupuesto ? <Text style={{ color: C.faint }}>{` de ${fmtE(o.presupuesto)}`}</Text> : null}
                </Text>
              </View>
              {pctO != null ? (
                <>
                  <View style={{ height: 5, borderRadius: 99, backgroundColor: C.card2, overflow: 'hidden', marginTop: 4 }}>
                    <View style={{ width: `${Math.min(100, Math.max(0, pctO))}%`, height: '100%', backgroundColor: pctO >= 100 ? C.good : C.warn }} />
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 2 }}>
                    <Text style={m.footNote}>{`${Math.round(pctO)}% del presupuesto`}</Text>
                    <Text style={[m.footNote, { color: (o.brecha ?? 0) < 0 ? C.bad : C.good }]}>
                      {o.brecha != null ? `${o.brecha > 0 ? '+' : ''}${fmtE(o.brecha)}` : ''}
                    </Text>
                  </View>
                </>
              ) : o.presupuesto ? (
                /* 🔴 SE CONTRADECÍA A SÍ MISMO. Ponía «— de 255.219 €» y debajo
                   «sin presupuesto cargado»: hay presupuesto, lo que falta es el
                   REAL. La condición exigía los dos y culpaba siempre al
                   presupuesto. Ahora se distingue, y con presupuesto se pinta la
                   barra a cero, que es la verdad. */
                <>
                  <View style={{ height: 5, borderRadius: 99, backgroundColor: C.card2, marginTop: 4 }} />
                  <Text style={m.footNote}>Sin facturación registrada este mes todavía</Text>
                </>
              ) : (
                <Text style={m.footNote}>Sin presupuesto cargado para este outlet</Text>
              )}
            </View>
            );
          })}
          <Text style={m.footNote}>
            Real del mes contra presupuesto. El mes en curso es provisional hasta que se publique el cierre.
          </Text>
        </View>
      ) : null}
    </Card>
  );
}


/* ══════════════════════════════════════════════════════════════════════════
   TOTALES DE GRUPO — la portada de Dirección General

   🔴 AQUÍ HABÍA UNA FICHA POR HOTEL Y ERA UN ERROR. Patricio, 25-08: «se sigue
   viendo sobrecargada la pantalla principal» y «sigo viendo muchos a 0 euros».
   Las dos cosas eran el mismo problema: 49 fichas, y las de los hoteles que no
   mandan facturación intradía abrían con un 0 € gigante. Una portada con 49
   fichas de presupuesto no es una portada, es un informe.
   Ahora la portada da TOTALES y el detalle vive en Hoteles. */
function TotalesGrupo() {
  const { dato, cargando, motivo } = useLive<import('@/api/client').ComercialLive>(
    () => api.comercial(), 10 * 60e3);
  /* 🔴 MIENTRAS CARGA, EL LATIDO — no la nada. Esto devolvía null durante los
     2-3 segundos del cálculo del comercial, así que la portada se pintaba SIN
     estas tarjetas y de golpe aparecían empujando lo demás hacia abajo
     («parece que no hay nada y de golpe te aparecen» — Patricio, 26-08, con el
     Ageing al lado cargando bien, que era el contraste que lo delataba). Un
     hueco que luego se rellena es un salto de página; una tarjeta que late dice
     «esto existe y está viniendo». El null se queda solo para el caso SIN
     datos (rol sin producción), donde no habrá tarjeta nunca. */
  if (cargando) {
    return <SinDato cargando titulo="Grupo contra presupuesto" motivo="Sumando los 39 hoteles de Control de Gestión…" />;
  }
  /* Fallo con motivo → se dice y se espera el reintento (12 s), no se esfuma:
     una tarjeta que desaparece parece que nunca existió. El null queda solo
     para «cargó bien y este rol no tiene nada aquí», que sí es no-tarjeta. */
  if (!dato?.hoteles?.length) {
    if (motivo) return <SinDato titulo="Grupo contra presupuesto" dot={C.warn} motivo={`${motivo} Reintentando…`} />;
    return null;
  }

  const hs = dato.hoteles;
  const suma = (f: (h: typeof hs[0]) => number | null | undefined) =>
    hs.reduce((n, h) => n + (f(h) ?? 0), 0);

  /* El mes en curso ya no se calcula aquí: lo trae `MesesGrupo` de su propio
     endpoint, que además da los meses anteriores. Se quitan las dos sumas en vez
     de dejarlas «por si acaso»: dos formas de calcular el mismo número acaban
     dando dos números distintos, y entonces hay que averiguar cuál es el bueno. */
  const anioReal = suma(h => h.anio?.real);
  const anioBud = suma(h => h.anio?.presupuesto);
  const outReal = suma(h => h.outlets.reduce((n, o) => n + (o.real ?? 0), 0));
  const outBud = suma(h => h.outlets.reduce((n, o) => n + (o.presupuesto ?? 0), 0));
  /* El % del mes transcurrido es el mismo para todos: se toma el de cualquiera
     que lo traiga, no se promedia — promediar porcentajes no significa nada. */
  const conBud = hs.filter(h => h.mes).length;
  /* 🔴 SI FALTA UN MES POR PUBLICAR EN CONTROL DE GESTIÓN, EL AÑO VA CORTO. Y sin
     decirlo parecería una caída del negocio en vez de un mes sin cerrar: es
     exactamente el tipo de fallo que se descubre delante del CEO. */
  const recalc = dato.fuente?.mesesRecalculados ?? [];

  return (
    <>
      {/* El número de hoteles va A LA VISTA: son los de Control de Gestión, los
          únicos con presupuesto 2026 cargado, y sin decirlo el total parece del
          grupo entero. */}
      {/* 🔴 Y SE DICE DE DÓNDE SALE EL NÚMERO, Y HASTA QUÉ DÍA (27-08). El mes sale
          de la facturación de Noray hasta ayer; el año, de la foto publicada de
          Control de Gestión más ese mes. Enseñar un acumulado sin decir de cuántos
          días es obliga a preguntar, y esta tarjeta ya costó cinco versiones por no
          declarar de dónde venía. */}
      {/* 🔴 AQUÍ HABÍA UNA SOLA TARJETA DEL MES EN CURSO, y el 1 de septiembre
          valía 0 € y 0 %. Ahora es un carrusel: enero…mes en curso, y se abre en
          el último mes CON producción. Los datos vienen de su propio endpoint
          (`/grupo-meses`) porque `comercial()` solo calcula el mes actual. */}
      <MesesGrupo />
      <BarraTotal titulo="Año del grupo contra presupuesto" real={anioReal} bud={anioBud}
        derecha={recalc.length
          ? `${conBud} hoteles · ${recalc.length === 1 ? 'un mes' : `${recalc.length} meses`} sin publicar, de facturación`
          : `${conBud} hoteles · CG + mes en curso`}
        detalle={hs.filter(h => h.anio).map(h => ({
          n: h.nombre, real: h.anio!.real, bud: h.anio!.presupuesto,
        }))} />
      {/* 🔴 LOS OUTLETS SE QUITAN DE LA PORTADA. Decisión de Patricio el 25-08:
          el real del mes en curso sale de la contabilidad, que todavía no tiene
          los asientos, así que la cifra enseñaría presupuestos contra huecos —
          se vio en Valle Romano, La Piazza y el Pool Bar del Ritual. Vuelve
          cuando el mes en curso se lea de Noray. «Es preferible mostrarle algo
          fiable, comprobable en Hotelos.» */}
    </>
  );
}

/* Una barra y dos cifras. Es la tarjeta que Patricio dijo que le parecía genial,
   así que se reusa tal cual para mes, año y outlets en vez de inventar tres.
 *
 * 🔴 Y AL TOCARLA, LOS HOTELES UNO A UNO. Patricio: «¿podemos hacer el efecto de
 * la imagen 2 y mostrar los hoteles con su budget y su real con una barra de
 * estado?». El total contra presupuesto contesta «cómo va el grupo»; la pregunta
 * siguiente es siempre «¿y quién lo está tirando?», y hasta ahora había que
 * salirse de la portada para saberlo.
 *
 * Ordenados por PORCENTAJE y no por euros: la lista sirve para ver quién va
 * flojo, y un hotel pequeño al 60 % importa más que uno grande al 99 %. Los
 * mejores arriba y los rezagados al final, que es donde el ojo acaba.
 */
/* ---------- El mes del grupo, mes a mes ----------
   Patricio, 01-09: «esta tarjeta estaría bueno que haya dos como tenemos arriba
   que muestre el mes en curso pero si desplazamos a la derecha que nos muestre
   meses anteriores, quizás poniéndole de título el mes que es».

   Y hoy se ve por qué: es día 1, así que la tarjeta del mes en curso enseña 0 €
   y 0 %. Honesto —lo declaraba con «el mes acaba de empezar»— pero inútil: el
   mes que importa el 1 de septiembre es agosto.

   🔴 CADA MES DICE DE DÓNDE SALE, porque no salen del mismo sitio: los cerrados
   y publicados, de la foto de Control de Gestión; los cerrados sin publicar, del
   folio de Noray; y el mes en curso, del folio hasta ayer. Un carrusel donde
   deslizar cambia la fuente sin avisar convierte una comparación en una trampa. */
function MesesGrupo() {
  const [w, setW] = useState(0);
  const [page, setPage] = useState(0);
  const puesto = useRef(false);
  const scroll = useRef<ScrollView>(null);
  const { dato, cargando, motivo } = useLive<import('@/api/client').GrupoMesesLive>(
    () => api.grupoMeses(), 10 * 60e3);

  const meses = dato?.meses ?? [];
  const inicial = dato?.inicial ?? Math.max(0, meses.length - 1);

  /* Se coloca en el mes de apertura UNA sola vez. Si se hiciera en cada render,
     deslizar a julio y que un refresco te devolviera a agosto sería pelearse con
     la tarjeta. */
  useEffect(() => {
    if (!w || !meses.length || puesto.current) return;
    puesto.current = true;
    setPage(inicial);
    requestAnimationFrame(() => scroll.current?.scrollTo({ x: inicial * w, animated: false }));
  }, [w, meses.length, inicial]);

  if (cargando) return <SinDato cargando titulo="Mes del grupo contra presupuesto" motivo="Sumando los meses del año…" />;
  if (!meses.length) {
    if (motivo) return <SinDato titulo="Mes del grupo contra presupuesto" dot={C.warn} motivo={`${motivo} Reintentando…`} />;
    return null;
  }

  const FUENTE: Record<string, string> = {
    cg: 'foto publicada de Control de Gestión',
    folio: 'facturación de Noray · CG aún no lo ha publicado',
    curso: 'facturación de Noray',
  };

  return (
    <View onLayout={(e) => setW(e.nativeEvent.layout.width)}>
      {w > 0 ? (
        <ScrollView
          ref={scroll}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          onMomentumScrollEnd={(e) => setPage(Math.round(e.nativeEvent.contentOffset.x / w))}
        >
          {meses.map((mm) => (
            <View key={mm.mes} style={{ width: w }}>
              <BarraTotal
                /* El título es el mes, como pidió. Y el año detrás: en enero,
                   «diciembre» sin año es el mes anterior o el de hace trece. */
                titulo={`${mm.nombre} ${dato?.anio ?? ''}`.trim()}
                real={mm.real} bud={mm.presupuesto}
                derecha={mm.fuente === 'curso'
                  ? (mm.ultimoDia
                    ? `${mm.hoteles} hoteles · al día ${mm.ultimoDia}`
                    : `${mm.hoteles} hoteles · el mes acaba de empezar`)
                  : `${mm.hoteles} hoteles · ${mm.fuente === 'cg' ? 'cerrado y publicado' : 'cerrado, sin publicar'}`}
                detalle={mm.detalle}
                pie={FUENTE[mm.fuente]}
              />
            </View>
          ))}
        </ScrollView>
      ) : null}
      {/* Los puntos, y el mes escrito al lado: unos puntos solos no dicen en qué
          mes estás si has deslizado tres veces. */}
      <View style={m.mesDots}>
        {meses.map((mm, i) => (
          <View key={mm.mes} style={[m.mesDot, i === page && m.mesDotOn]} />
        ))}
      </View>
    </View>
  );
}

function BarraTotal({ titulo, real, bud, derecha, detalle, pie }: {
  titulo: string; real: number; bud: number; derecha?: string;
  detalle?: Array<{ n: string; real: number; bud: number }>;
  /** De dónde sale el número de ESTE mes. Va a la vista, no en la documentación. */
  pie?: string;
}) {
  const [abierto, setAbierto] = useState(false);
  if (!bud) return null;
  const pct = (100 * real) / bud;
  const falta = Math.max(0, bud - real);
  /* Solo los que tienen presupuesto: dividir por cero pinta una barra infinita. */
  const filas = (detalle ?? []).filter(d => d.bud > 0)
    .map(d => ({ ...d, pct: (100 * d.real) / d.bud }))
    .sort((a, b) => b.pct - a.pct);

  const tarjeta = (
    <Card>
      <SectionTitle dot={pct >= 100 ? C.good : C.warn} right={derecha}>{titulo}</SectionTitle>
      <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 10, marginTop: 2 }}>
        <Text style={m.heroNum}>{fmtE(real)}</Text>
        <Chip kind={pct >= 100 ? 'good' : 'bad'}>{`${Math.round(pct)}%`}</Chip>
      </View>
      {/* La misma rampa que el detalle de dentro (Patricio, 26-08: «debemos usar
          el mismo degradé de cuando entramos»). Retiro lo del ámbar-veredicto:
          tener fuera un idioma y dentro otro para EL MISMO dato era peor que
          cualquiera de los dos idiomas. El veredicto lo sigue dando el chip. */}
      <View style={{ height: 8, borderRadius: 99, backgroundColor: C.card2, overflow: 'hidden', marginTop: 10 }}>
        <View style={{ width: `${Math.min(100, pct)}%`, height: '100%', backgroundColor: budgetColor(pct) }} />
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 5 }}>
        <Text style={m.legTxt}>{`de ${fmtE(bud)}`}</Text>
        <Text style={m.legTxt}>{falta > 0 ? `faltan ${fmtE(falta)}` : 'presupuesto cubierto'}</Text>
      </View>
      {/* De dónde sale ESTE número. En el carrusel de meses no es siempre el
          mismo sitio —CG publicado, folio de un mes sin publicar, folio del mes
          en curso— y deslizar sin que la fuente cambie a la vista convertiría una
          comparación en una trampa. */}
      {pie ? <Text style={m.footNote}>{pie}</Text> : null}
      {filas.length ? (
        <Text style={m.tbVolver}>{`toca para ver los ${filas.length} hoteles, uno a uno ▾`}</Text>
      ) : null}
    </Card>
  );

  if (!filas.length) return tarjeta;

  return (
    <>
      <Pressable onPress={() => setAbierto(true)}>{tarjeta}</Pressable>
      <ZoomDetalle
        open={abierto} onClose={() => setAbierto(false)}
        valor={`${Math.round(pct)}%`} etiqueta={titulo}
        pie={`${fmtE(real)} de ${fmtE(bud)}. Presupuesto de pl_budget_real, el mismo que lee Hotelos. `
           + 'Ordenados por % conseguido: los rezagados, al final.'}
      >
        {filas.map((d) => {
          const va = d.pct >= 100;
          /* 🔴 EL € NO SE PARTE (Patricio, 26-08: «para no poner debajo el símbolo €
             como Ritual, podés jugar acortando un poco más el nombre»). Dos medidas
             juntas: el nombre pierde los prefijos que no distinguen a nadie («Ona»,
             «Hotel», «Be Live» y los paréntesis de cadena) y se acota al 42 % de la
             fila; y el importe va a UNA línea con letra que se encoge sola si aun
             así no cabe (adjustsFontSizeToFit) — mejor una cifra un punto más
             pequeña que un € huérfano en la línea de abajo. */
          const nom = d.n.replace(/^(Ona|Hotel|Be ?live)\s+/i, '').replace(/\s*\(.*\)\s*$/, '');
          return (
            <View key={d.n} style={{ paddingVertical: 6 }}>
              <View style={m.miniRow}>
                <Text style={[m.miniLbl, { maxWidth: '42%' }]} numberOfLines={1}>{nom}</Text>
                <Text style={[m.miniVal, { flex: 1 }]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.72}>
                  {fmtE(d.real)}
                  <Text style={{ color: C.faint }}>{` de ${fmtE(d.bud)}`}</Text>
                </Text>
              </View>
              {/* La rampa de la marca en vez del ámbar: nueve barras del mismo
                  color no se comparan, y con el degradé el ojo ordena la lista
                  solo. El ámbar se queda en la tarjeta grande, que da UN veredicto
                  («llega» / «no llega») y ahí el color significa algo distinto. */}
              <View style={{ height: 5, borderRadius: 99, backgroundColor: C.card2, overflow: 'hidden', marginTop: 4 }}>
                <View style={{ width: `${Math.min(100, Math.max(0, d.pct))}%`, height: '100%', backgroundColor: budgetColor(d.pct) }} />
              </View>
              {/* 🔴 TODOS LOS NÚMEROS EN LA MISMA COLUMNA DE LA DERECHA (Patricio,
                  26-08). El importe ya iba a la derecha y el porcentaje se quedaba
                  a la izquierda, así que había que cruzar la fila con la vista para
                  leer las dos cifras de un mismo hotel. Y «budget» en vez de
                  «presupuesto» porque es la mitad de largo y aquí lo que sobra es
                  ancho — es además la palabra que usa el resto de la app. */}
              <Text style={[m.footNote, { marginTop: 2, textAlign: 'right', color: va ? C.aqua : C.faint }]}>
                {`${Math.round(d.pct)}% del budget`}
              </Text>
            </View>
          );
        })}
      </ZoomDetalle>
    </>
  );
}

/* ---------- RRHH ---------- */

function RrhhCoste() {
  const d = ((G.rrhh.coste - G.rrhh.bud) / G.rrhh.bud) * 100;
  return (
    <Card demo>
      <SectionTitle>Coste de personal · julio</SectionTitle>
      <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 10 }}>
        <Text style={m.heroNum}>{fmtM(G.rrhh.coste)}</Text>
        <Chip kind="good">{`${fmtDec(d)}% vs budget`}</Chip>
      </View>
      <View style={{ marginTop: 12, gap: 5 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Text style={m.legTxt}>Real</Text>
          <Text style={m.legTxt}>Budget {fmtM(G.rrhh.bud)}</Text>
        </View>
        <Bar pct={(G.rrhh.coste / G.rrhh.bud) * 100} height={10} />
      </View>
      <View style={m.chipRow}>
        <Chip>{`ratio s/TNR ${pct1(G.rrhh.ratio)}`}</Chip>
        <Chip>Payroll budget por outlet</Chip>
      </View>
    </Card>
  );
}

function Plantilla() {
  return (
    <Card demo>
      <SectionTitle>Plantilla</SectionTitle>
      <Row icon={I('people')} label="Personas activas" value={fmt(G.rrhh.plantilla)} chip={`▲ +${G.rrhh.dPl}`} chipKind="good" />
      <Row icon={I('alarm')} label="Horas extra · julio" value={`${fmt(G.rrhh.hextra)} h`} chip="Palmira 214 h" chipKind="warn" />
      <Row icon={I('sync')} label="Rotación YTD" value="11,4%" chip="▼ −1,2 pts" chipKind="good" last />
    </Card>
  );
}

/* ---------- Asset ---------- */

function AssetKpis() {
  return (
    <View style={{ gap: 10 }}>
      <View style={m.kpis}>
        <KpiTile value={String(G.asset.activos)} label="Activos en cartera" delta="3 países" deltaKind="flat" />
        <KpiTile value={fmt(G.asset.uds)} label="Unidades" delta={`${fmt(G.asset.enlazadas)} enlazadas`} deltaKind="flat" />
      </View>
      <View style={m.kpis}>
        <KpiTile value={String(G.asset.contratos)} label="Contratos condohotel" delta="explotación" deltaKind="flat" />
        <KpiTile value={fmtM(G.asset.renta)} label="Renta + fees anual" delta="▲ +2,8%" />
      </View>
    </View>
  );
}

function Vencimientos() {
  const { dato, cargando, motivo } = useLive<AssetKpisLive>(() => api.assetKpis(), 30 * 60e3);
  if (cargando) return <SinDato cargando titulo="Próximos vencimientos" motivo="Leyendo los contratos…" />;
  const v = dato?.vencimientos ?? null;
  if (!v) return <SinDato titulo="Próximos vencimientos" motivo={motivo ?? 'No hay acceso a los contratos de Asset Management.'} />;

  const fecha = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  const cuando = (mm: number) => (mm < 0
    ? `venció hace ${Math.abs(mm)} ${Math.abs(mm) === 1 ? 'mes' : 'meses'}`
    : mm === 0 ? 'vence este mes' : `en ${mm} ${mm === 1 ? 'mes' : 'meses'}`);
  /* Los vencidos primero: un contrato ACTIVO con la fecha pasada es lo más urgente
     que puede tener esta tarjeta — o se renovó y nadie lo apuntó, o se está
     operando sin contrato. */
  const lista = [...v.vencidos, ...v.proximos].slice(0, 6);

  return (
    <Card>
      <SectionTitle dot={v.vencidos.length ? C.bad : C.warn}
        right={`${v.en24meses} en 24 meses`}>Próximos vencimientos</SectionTitle>
      {!lista.length ? (
        <Text style={m.sinDato}>Ningún contrato con fecha de fin legible.</Text>
      ) : (
        <View style={m.tl}>
          {lista.map((x, i) => (
            <View key={`${x.nombre}-${x.fin}-${i}`} style={m.tlEv}>
              <View style={[m.tlDot, { borderColor: x.meses < 0 ? C.bad : x.meses <= 12 ? C.warn : C.aqua }]} />
              <Text style={m.tbName} numberOfLines={1}>
                {`${x.nombre}${x.tipo ? ` · ${x.tipo}` : ''}`}
              </Text>
              <Text style={m.subTxt}>
                {`${fecha(x.fin)} · ${cuando(x.meses)}${x.renta ? ` · ${x.renta}` : ''}`}
              </Text>
            </View>
          ))}
        </View>
      )}
      {/* 🔴 LO QUE NO SE PUDO LEER SE DICE. `am_contratos.finalizacion` es texto
          libre: hay fechas vacías y alguna basura de captura («1905-07-16» en
          Hacienda del Álamo, medido el 30-08). Un contrato ilegible que desaparece
          en silencio de esta lista es justo el que caduca sin que nadie avise. */}
      {(v.sinFecha || v.absurdas) ? (
        <Text style={m.footNote}>
          {`De ${v.total} contratos activos, ${v.sinFecha + v.absurdas} no tienen fecha de fin legible`}
          {v.absurdas ? ` (${v.absurdas} con una fecha imposible)` : ''}
          {'. No salen aquí y conviene arreglarlos en Hotelos.'}
        </Text>
      ) : null}
    </Card>
  );
}

function Unlink() {
  const { dato, cargando, motivo } = useLive<AssetKpisLive>(() => api.assetKpis(), 30 * 60e3);
  if (cargando) return <SinDato cargando titulo="Calidad del dato" motivo="Contando lo que falta por enlazar…" />;
  const c = dato?.calidad ?? null;
  if (!c) return <SinDato titulo="Calidad del dato" motivo={motivo ?? 'No hay acceso al inventario de unidades.'} />;
  return (
    <Card>
      <SectionTitle right={`${fmt(c.unidades)} unidades`}>Calidad del dato</SectionTitle>
      <Row icon={I('link')} label="Unidades sin enlazar a hotel" value={fmt(c.sinHotel)}
        chip={c.sinHotel ? 'revisar' : 'al día'} chipKind={c.sinHotel ? 'warn' : 'good'} />
      <Row icon={I('pin')} label="Hoteles sin coordenadas" value={String(c.sinCoordenadas)}
        chip={c.sinCoordenadas ? 'mapa' : 'al día'} chipKind={c.sinCoordenadas ? 'warn' : 'good'} last />
      {/* Con los nombres delante se arregla en el momento; con solo el número hay
          que salirse de la app a buscar cuáles son. */}
      {c.hotelesSinCoordenadas.length ? (
        <Text style={m.footNote}>{`Sin coordenadas: ${c.hotelesSinCoordenadas.join(' · ')}`}</Text>
      ) : null}
    </Card>
  );
}

/* ---------- composición por rol ---------- */

export function RoleModules({ role, hoteles, brief }: { role: RoleId; hoteles?: HotelRow[]; brief?: Brief | null }) {
  switch (role) {
    case 'dg':
      /* Sin AlertPeek: «requiere tu atención a dirección general en la pantalla
         principal no, ya tiene la campanilla» — Patricio, 25-08. */
      return (<><KpiTrio brief={brief} /><TotalesGrupo /><Ageing vista30 /><TopBottom live={hoteles} /></>);
    case 'dh':
      return (<><KpiTrio brief={brief} /><Comercial /><MiHotel hoteles={hoteles} /><Heat /><AlertPeek /></>);
    case 'reg':
      return (<><KpiTrio brief={brief} /><TotalesGrupo /><MisHoteles hoteles={hoteles} /><Heat /><AlertPeek /></>);
    case 'ops':
      return (<><OpsToday /><Rooms /><Incid /><AlertPeek filter="ops" /></>);
    case 'rev':
      /* 🔴 REVENUE TAMBIÉN VE EL GRUPO CONTRA PRESUPUESTO (30-08, pedido de
         Patricio). Y tiene sentido: quien mueve el precio y el pickup es quien
         más necesita saber si el mes va a llegar — hasta ahora tenía que
         preguntárselo a dirección. */
      return (<><KpiTrio brief={brief} /><TotalesGrupo /><Pickup /><Heat /><Agencias /></>);
    case 'fin':
      return (<><Cash /><Ageing /><Cierres /><Club /></>);
    case 'sis':
      return (<><PmsHealth /><SisKpis /><Syncs /><PremioCard /><NoaAprende /><AlertPeek filter="sis" /></>);
    case 'rrhh':
      return (<><RrhhCoste /><Plantilla /></>);
    case 'ast':
      return (<><AssetKpis /><Vencimientos /><Unlink /></>);
  }
}

const m = StyleSheet.create({
  /* Filas de detalle del cuadro de mandos comercial. */
  miniRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 10, paddingVertical: 4 },
  miniLbl: { fontFamily: F.ui, fontSize: 12, color: C.muted, flexShrink: 1 },
  miniVal: { fontFamily: F.uiSemi, fontSize: 12.5, color: C.ink, fontVariant: ['tabular-nums'], textAlign: 'right' },
  kpis: { flexDirection: 'row', gap: 10 },
  heroNum: { fontFamily: F.display, fontSize: 32, color: '#fff' },
  subTxt: { fontFamily: F.ui, fontSize: 11, color: C.muted, marginTop: 2, lineHeight: 15 },
  footNote: { fontFamily: F.ui, fontSize: 10, color: C.faint, marginTop: 10, lineHeight: 14 },
  /* Las cuatro de Sistemas, a ancho completo. La cifra manda y debajo va la línea
     que dice QUÉ pasa: sin ella un contador no es accionable. */
  sisNum: { fontFamily: F.display, fontSize: 30, color: C.ink, fontVariant: ['tabular-nums'] },
  sisUnit: { fontFamily: F.ui, fontSize: 13.5, color: C.muted, flexShrink: 1 },
  sisPie: { fontFamily: F.ui, fontSize: 11, color: C.muted, marginTop: 1 },
  sisBarra: { height: 8, borderRadius: 4, backgroundColor: C.bg2, overflow: 'hidden', marginTop: 10 },
  sisLinea: { fontFamily: F.uiMed, fontSize: 12.5, color: C.muted, marginTop: 9, lineHeight: 17 },
  tbVolver: { fontFamily: F.uiMed, fontSize: 11, color: C.aquaSoft, marginTop: 10, lineHeight: 15 },
  tramoT: { fontFamily: F.uiSemi, fontSize: 12, color: C.ink, flex: 1 },
  tramoV: { fontFamily: F.uiBold, fontSize: 12, color: C.muted, fontVariant: ['tabular-nums'] },
  cliRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 10, paddingVertical: 4, paddingLeft: 17 },
  /* 🔴 EL NOMBRE EMPUJA, EL IMPORTE NO SE MUEVE (Patricio, 26-08: «los números
     tienen que estar alineados»). Antes el nombre solo encogía (flexShrink) y el
     importe iba suelto en el space-between: cada fila acababa con su cifra en un
     sitio. Con el nombre a flex:1 y el importe en columna fija alineada a la
     derecha, los millares caen unos debajo de otros, que es lo que permite
     compararlos de un vistazo. */
  cliN: { fontFamily: F.ui, fontSize: 11.5, color: C.muted, flex: 1 },
  cliV: { fontFamily: F.uiSemi, fontSize: 11.5, color: C.ink, fontVariant: ['tabular-nums'], width: 104, textAlign: 'right' },
  cliF: { fontFamily: F.ui, fontSize: 11, color: C.faint, width: 46, textAlign: 'right' },
  ensenaCta: { fontFamily: F.uiSemi, fontSize: 11.5, color: C.aquaSoft },
  hojaT: { fontFamily: F.displayMed, fontSize: 20, color: C.ink },
  hojaFila: { paddingVertical: 9, borderBottomColor: C.line, borderBottomWidth: 1 },
  hojaN: { fontFamily: F.uiMed, fontSize: 12.5, color: C.ink, flex: 1 },
  hojaMedia: { fontFamily: F.uiBold, fontSize: 12, color: C.muted, fontVariant: ['tabular-nums'] },
  errTxt: { fontFamily: F.ui, fontSize: 11, color: C.bad, marginTop: 4, lineHeight: 16 },
  falloFila: { paddingVertical: 7, paddingLeft: 26 },
  falloQue: { fontFamily: F.uiSemi, fontSize: 12, color: C.ink },
  verEnsenado: { fontFamily: F.uiSemi, fontSize: 12, color: C.aquaSoft },
  sinDato: { fontFamily: F.ui, fontSize: 12, color: C.muted, lineHeight: 17, marginTop: 2 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  wfRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  wfLabel: { width: 100, fontFamily: F.ui, fontSize: 11.5, color: C.muted },
  wfTrack: { flex: 1, height: 10, borderRadius: 6, backgroundColor: 'rgba(140,200,215,0.08)' },
  wfVal: { width: 64, textAlign: 'right', fontFamily: F.uiBold, fontSize: 11.5, color: C.ink, fontVariant: ['tabular-nums'] },
  tbRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: C.line },
  tbMedal: { width: 26, textAlign: 'center', fontSize: 15, color: C.muted },
  tbName: { flex: 1, fontFamily: F.uiMed, fontSize: 13, color: C.ink },
  habLbl: { fontFamily: F.uiSemi, fontSize: 10, letterSpacing: 0.8, color: C.aquaSoft, marginBottom: 4 },
  habHero: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 12 },
  mesDots: { flexDirection: 'row', justifyContent: 'center', gap: 5, paddingTop: 8, flexWrap: 'wrap' },
  mesDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: 'rgba(140,200,215,0.28)' },
  mesDotOn: { backgroundColor: C.aqua, width: 16 },
  habPct: { fontFamily: F.display, fontSize: 34, color: C.ink, fontVariant: ['tabular-nums'] },
  habHeroT: { fontFamily: F.uiSemi, fontSize: 13, color: C.ink },
  habBar: { flexDirection: 'row', height: 6, borderRadius: 99, backgroundColor: C.card2, overflow: 'hidden', marginTop: 5 },
  habPie: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 },
  habPieL: { flex: 1, fontFamily: F.ui, fontSize: 10, color: C.faint, lineHeight: 14 },
  habPieV: { fontFamily: F.uiBold, fontSize: 10.5, color: C.bad, fontVariant: ['tabular-nums'] },
  habVal: { fontFamily: F.uiBold, fontSize: 13, color: C.ink, fontVariant: ['tabular-nums'], textAlign: 'right', minWidth: 54 },
  legGrid: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 12, rowGap: 7 },
  legItem: { flexDirection: 'row', alignItems: 'center', gap: 7, width: '50%' },
  legDot: { width: 9, height: 9, borderRadius: 3 },
  legTxt: { fontFamily: F.ui, fontSize: 11.5, color: C.muted },
  legVal: { fontFamily: F.uiBold, color: C.ink },
  heatName: { width: 104, fontFamily: F.ui, fontSize: 10, color: C.muted, paddingRight: 8 },
  heatLeg: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 10 },
  heatLegCell: { width: 14, height: 9, borderRadius: 3 },
  cierreDot: { width: 14, height: 14, borderRadius: 5 },
  pmsCell: { width: '47%', flexGrow: 1, backgroundColor: C.bg2, borderColor: C.line, borderWidth: 1, borderRadius: 16, padding: 12 },
  pmsName: { fontFamily: F.uiSemi, fontSize: 13, color: C.ink },
  pmsMsg: { fontFamily: F.ui, fontSize: 10.5, color: C.muted, marginTop: 6, lineHeight: 15 },
  tl: { borderLeftWidth: 2, borderLeftColor: 'rgba(30,211,199,0.25)', marginLeft: 7, paddingLeft: 18, gap: 14 },
  tlEv: {},
  tlDot: { position: 'absolute', left: -24.5, top: 3, width: 11, height: 11, borderRadius: 6, backgroundColor: C.card, borderWidth: 2.5 },
});
